import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Plus, Search, Settings as SettingsIcon, Sprout, LogOut, ArrowDownAZ, ArrowUpZA, Layers, ChevronDown, Clock, ChevronLeft, ChevronRight, HardDrive, LayoutGrid, Grid2x2, List, Sparkles, PencilLine, Archive, Image as ImageIcon, Box, Share2, Link2, Eye, Trash2, QrCode, Moon, X as XIcon, SlidersHorizontal, Globe, CloudOff, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuCheckboxItem, DropdownMenuLabel, DropdownMenuSeparator } from '../components/ui/dropdown-menu';
import { Input } from '../components/ui/input';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';
import { PlantCard } from '../components/PlantCard';
import { AddPlantModal } from '../components/AddPlantModal';
import { PlantDetailModal } from '../components/PlantDetailModal';
import { SettingsModal } from '../components/SettingsModal';
import { BackfillNotifierChip } from '../components/BackfillNotifierChip';
import { FamilyGroupingTip } from '../components/FamilyGroupingTip';
import { LastSyncedChip } from '../components/LastSyncedChip';
import { PendingSyncPill } from '../components/PendingSyncPill';
import { useOfflineQueue } from '../hooks/useOfflineQueue';
import { OnboardingTour } from '../components/OnboardingTour';
import { InviteQrModal } from '../components/InviteQrModal';
import { ShareChoiceModal } from '../components/ShareChoiceModal';
import { MigrationDialog } from '../components/MigrationDialog';
import { BulkEditBar } from '../components/BulkEditBar';
import { ArchiveDialog } from '../components/ArchiveDialog';
import { MergePlantsDialog } from '../components/MergePlantsDialog';
import { MergeTargetPicker } from '../components/MergeTargetPicker';
import { DuplicateSuggestionCard } from '../components/DuplicateSuggestionCard';
import { formatRelativeTime } from '../utils/relativeTime';
import * as localStore from '../services/localPlantStore';
import { useDriveConnection } from '../hooks/useDriveConnection';
import { usePlantData } from '../hooks/usePlantData';
import { useViewMode, GROUP_OPTIONS, LIST_PER_PAGE_OPTIONS } from '../hooks/useViewMode';
import { useDraggableFab } from '../hooks/useDraggableFab';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

import { API_BASE_URL } from '../utils/apiBase';
const BACKEND_URL = API_BASE_URL;

// Map the cardSize setting to its Tailwind masonry-grid class.
const getCardGridClass = (cardSize) => {
  if (cardSize === 'list') return 'masonry-grid-list rounded-xl overflow-hidden border';
  if (cardSize === 'compact') return 'masonry-grid-compact';
  return 'masonry-grid';
};

// Module-level cache so React StrictMode's dev double-mount doesn't cause
// the share-target inbox to be popped twice (which would lose the payload)
// or race between the two mount instances. The promise is created once
// per page load; both mounts await the same result.
let _shareTargetPromise = null;

export const LibraryPage = () => {
  const { user, isLocalMode, logout: authLogout, exitLocalMode } = useAuth();
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, language, setLanguage, autoDetected, dismissAutoDetected } = useLanguage();

  // Human-readable name of the auto-detected language, used in the
  // dismiss chip so the user sees exactly what was picked. Falls back
  // to the raw language code when we don't have a friendly name.
  const AUTO_DETECT_LANG_NAMES = { es: 'Español', fr: 'Français', de: 'Deutsch', pt: 'Português', ja: '\u65e5\u672c\u8a9e', ko: '\ud55c\uad6d\uc5b4', zh: '\u4e2d\u6587' };
  const autoDetectedLangName = autoDetected ? (AUTO_DETECT_LANG_NAMES[language] || language) : null;
  const switchBackToEnglish = useCallback(() => {
    setLanguage('en');
    toast.success(t('langAutoDetectedSwitchToast') || 'Language switched to English');
  }, [setLanguage, t]);

  // Offline browsing (iter 94, option A) — read-only fallback when the
  // network is down. Mirror state comes from `usePlantData` (see the
  // hook for details); we surface a soft indigo banner here so the
  // user knows they're browsing a snapshot and can't currently save
  // edits. Also live-tracks `navigator.onLine`.
  const [isOnline, setIsOnline] = useState(
    () => typeof navigator !== 'undefined' ? navigator.onLine : true
  );
  useEffect(() => {
    const onOnline = () => setIsOnline(true);
    const onOffline = () => setIsOnline(false);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  // Custom hooks
  const { driveConnected, checkingDrive, handleConnectDrive } = useDriveConnection(isLocalMode);

  const {
    plants, filteredPlants, incompletePlants, loading,
    searchQuery, setSearchQuery,
    showTerrariumOnly, setShowTerrariumOnly,
    showDormantOnly, setShowDormantOnly,
    showNeedsDormancy, setShowNeedsDormancy,
    familyFilter, setFamilyFilter,
    lastSyncedAt,
    refreshing, refreshPlants,
    localPlantTotal, exporting,
    showMigration, setShowMigration, localPlantCount,
    handlePlantAdded: onPlantAdded, handlePlantDeleted: onPlantDeleted, handleExportCSV, handleExportPDF, handleShareLibrary, handleCopyInvite, handlePreviewInvite,
    reloadAfterMigration,
  } = usePlantData(isLocalMode, driveConnected, user);

  // Offline edit queue (Drive mode only). Stores edit/archive/delete
  // made while offline and auto-flushes on reconnect. The mirror user
  // key is persisted so the axios interceptor can attribute queued
  // writes to the right account.
  const offlineUserKey = user?.email || user?.id || 'anon';
  useEffect(() => {
    if (!isLocalMode) {
      try { localStorage.setItem('plant_mirror_user_key', offlineUserKey); } catch { /* quota */ }
    }
  }, [isLocalMode, offlineUserKey]);
  const { pending: pendingSync, pendingCount: pendingSyncCount, flushing: pendingFlushing, flush: flushPendingSync, discard: discardPendingSync, discardAll: discardAllPendingSync, justSynced: pendingJustSynced, progress: pendingProgress } =
    useOfflineQueue(offlineUserKey, !isLocalMode, onPlantAdded, {
      undo: t('discardUndo') || 'Undo',
      discarded: t('changeDiscarded') || 'Change discarded',
      discardedAll: t('pendingSyncDiscardAllToast') || 'Discarded all pending changes',
      syncedOne: t('syncSyncedOne') || '1 change synced',
      syncedMany: t('syncSyncedMany') || '{n} changes synced',
      conflictOne: t('syncConflictOne') || 'Couldn\u2019t sync "{name}" \u2014 it was changed or removed on another device.',
      conflictMany: t('syncConflictMany') || '{n} changes couldn\u2019t sync \u2014 those plants were changed or removed on another device.',
      gaveupOne: t('syncGaveupOne') || 'Gave up syncing "{name}" after several tries \u2014 please redo it.',
      gaveupMany: t('syncGaveupMany') || '{n} changes couldn\u2019t be saved after several tries and were dropped.',
      retryOne: t('syncRetryOne') || '1 change couldn\u2019t sync \u2014 will retry',
      retryMany: t('syncRetryMany') || '{n} changes couldn\u2019t sync \u2014 will retry',
    });

  const {
    viewMode, sortAsc, cardSize, setCardSize, groupBy,
    // iter 71 — unified sort/group model
    sortBy, setSortBy, thenBy, setThenBy, showDateAdded,
    perPage, setPerPage, currentPage, setCurrentPage,
    PER_PAGE_OPTIONS,
    totalComplete, totalPages, safePage, startIndex, paginatedPlants,
    getGroupedPlants,
  } = useViewMode(filteredPlants, searchQuery, setSearchQuery);

  // Short "Added <date>" label, shown on cards/rows when a date sort is active.
  const formatAddedDate = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    if (isNaN(d)) return null;
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  };


  // Modal / dialog state (kept in component — tightly coupled to JSX)
  const [showAddModal, setShowAddModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [warmingPhotos, setWarmingPhotos] = useState(false);
  const [pendingDialogOpen, setPendingDialogOpen] = useState(false);
  // Onboarding tour — auto-shown on first run when the library is empty
  // AND the user hasn\u2019t already seen it. Can also be replayed anytime
  // from Settings via a "Watch onboarding tour" link.
  const [showOnboardingTour, setShowOnboardingTour] = useState(false);
  const [selectedPlant, setSelectedPlant] = useState(null);
  const [editingPlant, setEditingPlant] = useState(null);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [qrInviteUrl, setQrInviteUrl] = useState(null);
  const [shareChoiceOpen, setShareChoiceOpen] = useState(false);
  // Merge feature state — `mergeSource` is the plant being merged in.
  // When it's set and `mergeTarget` is null, the target picker is shown.
  // When both are set, the diff dialog opens.
  const [mergeSource, setMergeSource] = useState(null);
  const [mergeTarget, setMergeTarget] = useState(null);
  // Duplicate suggestion dismissals — split into two persistent sets:
  //   • `permanentlyNotDupePairs`: user tapped "Not a duplicate" — never
  //     suggest this pair again.
  //   • `reviewLaterDupePairs`: user tapped "Review later" — pair is
  //     hidden from the library banner but appears in Settings ▸
  //     Duplicate review for later triage.
  // Legacy `dismissed_dupe_pairs` (pre-Feb 2026) gets migrated once into
  // the "review later" bucket so existing dismissals aren't lost.
  const [permanentlyNotDupePairs, setPermanentlyNotDupePairs] = useState(() => {
    try {
      const raw = localStorage.getItem('not_dupe_pairs');
      return raw ? new Set(JSON.parse(raw)) : new Set();
    } catch { return new Set(); }
  });
  const [reviewLaterDupePairs, setReviewLaterDupePairs] = useState(() => {
    try {
      const modern = localStorage.getItem('review_later_dupe_pairs');
      if (modern) return new Set(JSON.parse(modern));
      // One-time migration from the legacy key.
      const legacy = localStorage.getItem('dismissed_dupe_pairs');
      if (legacy) {
        try { localStorage.setItem('review_later_dupe_pairs', legacy); } catch { /* quota — safe to ignore */ }
        return new Set(JSON.parse(legacy));
      }
      return new Set();
    } catch { return new Set(); }
  });
  const dismissedDupePairs = useMemo(
    () => new Set([...permanentlyNotDupePairs, ...reviewLaterDupePairs]),
    [permanentlyNotDupePairs, reviewLaterDupePairs]
  );
  // Duplicate-banner master switch. When false the banner never renders
  // no matter how many fuzzy hits there are. Default: enabled.
  const [dupeBannerEnabled, setDupeBannerEnabled] = useState(
    () => localStorage.getItem('dupe_banner_enabled') !== 'false'
  );
  // Fuzzy-match strictness. 'balanced' (default) uses the current
  // substring + Jaccard≥0.66 heuristic. 'strict' bumps Jaccard to 0.85
  // and drops the substring shortcut. 'exact' skips fuzzy entirely and
  // only pairs plants whose normalised common name is identical.
  const [dupeStrictness, setDupeStrictness] = useState(
    () => {
      const v = localStorage.getItem('dupe_strictness');
      return v === 'strict' || v === 'exact' ? v : 'balanced';
    }
  );
  // Snooze — one-tap "hide banner for 7 days" chip on the library.
  // Stored as an ISO timestamp; the banner short-circuits while
  // Date.now() < snoozeUntil. Cleared automatically when the deadline
  // passes on next mount / re-render.
  const [dupeSnoozeUntil, setDupeSnoozeUntil] = useState(
    () => {
      const raw = localStorage.getItem('dupe_banner_snoozed_until');
      if (!raw) return null;
      const ts = Number(raw);
      return Number.isFinite(ts) && ts > Date.now() ? ts : null;
    }
  );
  // Cross-device sync of the two dupe-dismissal lists — Drive mode only.
  // On mount we UNION server + local so nothing gets lost when a user
  // signs in on a new device; on subsequent state changes we debounce a
  // PUT /api/settings so the server always reflects the latest lists.
  const _dupeSyncBootedRef = useRef(false);
  const _dupeSyncTimerRef = useRef(null);
  useEffect(() => {
    if (isLocalMode) return;
    const sessionToken = localStorage.getItem('auth_hint');
    if (!sessionToken) return;
    if (_dupeSyncBootedRef.current) return;
    (async () => {
      let serverNotDupe = [];
      let serverReview = [];
      try {
        const resp = await axios.get(`${BACKEND_URL}/api/settings`, {
          headers: { Authorization: `Bearer ${sessionToken}` },
        });
        serverNotDupe = Array.isArray(resp.data?.not_dupe_pairs) ? resp.data.not_dupe_pairs : [];
        serverReview = Array.isArray(resp.data?.review_later_pairs) ? resp.data.review_later_pairs : [];
        if (serverNotDupe.length) {
          setPermanentlyNotDupePairs(prev => {
            const merged = new Set(prev);
            for (const k of serverNotDupe) merged.add(k);
            try { localStorage.setItem('not_dupe_pairs', JSON.stringify([...merged])); } catch { /* quota — safe */ }
            return merged;
          });
        }
        if (serverReview.length) {
          setReviewLaterDupePairs(prev => {
            const merged = new Set(prev);
            for (const k of serverReview) merged.add(k);
            try { localStorage.setItem('review_later_dupe_pairs', JSON.stringify([...merged])); } catch { /* quota — safe */ }
            return merged;
          });
        }
      } catch {
        // Non-blocking — local lists still work; the effect that PUTs on
        // state change will retry once the user makes any dismissal.
      }
      // Mark booted so subsequent state changes push. Do this AFTER the
      // union setStates so any React scheduling for those updates lands
      // before the push effect re-fires.
      _dupeSyncBootedRef.current = true;
      // Force a one-shot push of the local set so any entries that
      // pre-date cross-device sync (or were marked before the GET landed)
      // get uploaded to the server. Without this, a first-time dismissal
      // on a new device could sit in localStorage forever and never
      // reach the other devices — that's the "keeps coming back" bug.
      try {
        const localNotDupe = JSON.parse(localStorage.getItem('not_dupe_pairs') || '[]');
        const localReview = JSON.parse(localStorage.getItem('review_later_dupe_pairs') || '[]');
        const missingNotDupe = localNotDupe.some(k => !serverNotDupe.includes(k));
        const missingReview = localReview.some(k => !serverReview.includes(k));
        if (missingNotDupe || missingReview) {
          await axios.put(`${BACKEND_URL}/api/settings`, {
            not_dupe_pairs: Array.from(new Set([...serverNotDupe, ...localNotDupe])),
            review_later_pairs: Array.from(new Set([...serverReview, ...localReview])),
          }, { headers: { Authorization: `Bearer ${sessionToken}` } });
        }
      } catch {
        // Non-blocking — next user-triggered change will retry.
      }
    })();
  }, [isLocalMode]);
  // Debounced push whenever either list changes AFTER the boot GET
  // completes. Previously this skipped the very first render after boot
  // to avoid overwriting freshly-hydrated server state, but that also
  // silently dropped the user's first dismissal on a device — the
  // "marked as not a duplicate but keeps coming back" bug.
  useEffect(() => {
    if (isLocalMode) return;
    const sessionToken = localStorage.getItem('auth_hint');
    if (!sessionToken) return;
    if (!_dupeSyncBootedRef.current) return;
    if (_dupeSyncTimerRef.current) clearTimeout(_dupeSyncTimerRef.current);
    _dupeSyncTimerRef.current = setTimeout(async () => {
      try {
        await axios.put(`${BACKEND_URL}/api/settings`, {
          not_dupe_pairs: [...permanentlyNotDupePairs],
          review_later_pairs: [...reviewLaterDupePairs],
        }, { headers: { Authorization: `Bearer ${sessionToken}` } });
      } catch {
        // Non-blocking — local state is still authoritative for this
        // session; next change re-attempts the push.
      }
    }, 1200);
    return () => { if (_dupeSyncTimerRef.current) clearTimeout(_dupeSyncTimerRef.current); };
  }, [isLocalMode, permanentlyNotDupePairs, reviewLaterDupePairs]);
  // If a plant detail modal wants to share a specific plant, it sets this
  // ref before opening the share chooser so Option 1 targets that plant.
  const [shareChoicePlant, setShareChoicePlant] = useState(null);
  const [sharedInitialFile, setSharedInitialFile] = useState(null);
  // Draft-autosave for the batch add-plant queue. `draftInfo` holds
  // metadata for the Resume banner; `resumeDraft` is the payload passed
  // into AddPlantModal when the user taps Resume. Loaded from IndexedDB
  // on mount and after every add-modal close.
  const [draftInfo, setDraftInfo] = useState(null);
  const [resumeDraft, setResumeDraft] = useState(null);
  // One-session "Dismiss" — hides the Resume banner without touching the
  // draft, so it stays available on the next visit.
  const [draftBannerDismissed, setDraftBannerDismissed] = useState(false);
  // After a batch, "Edit" on the summary opens the full editor for that plant.
  // We stash the id and open the editor once the refreshed list contains it.
  const [pendingEditPlantId, setPendingEditPlantId] = useState(null);
  useEffect(() => {
    if (!pendingEditPlantId) return;
    const p = plants.find(x => x.plant_id === pendingEditPlantId);
    if (p) {
      setPendingEditPlantId(null);
      setEditingPlant(p);
      setShowAddModal(true);
    }
  }, [pendingEditPlantId, plants]);
  const _refreshDraft = useCallback(async () => {
    try {
      const { loadDraft } = await import('../utils/addPlantDraft');
      const d = await loadDraft();
      setDraftInfo(d ? { count: d.files.length, mode: d.mode, createdAt: d.createdAt } : null);
    } catch { /* no-op */ }
  }, []);
  useEffect(() => { _refreshDraft(); }, [_refreshDraft]);
  const [plantToDelete, setPlantToDelete] = useState(null);
  const [backfilling, setBackfilling] = useState(false);
  const [backfillDismissed, setBackfillDismissed] = useState(false);
  // Dormancy banner dismissal is remembered per calendar month so the nudge
  // returns when a new dormancy window starts, but doesn't nag the same user
  // multiple times in October.
  const currentMonthKey = `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`;
  const [dormancyBannerDismissed, setDormancyBannerDismissed] = useState(
    () => localStorage.getItem('dormancy_banner_dismissed') === currentMonthKey
  );
  const dismissDormancyBanner = useCallback(() => {
    localStorage.setItem('dormancy_banner_dismissed', currentMonthKey);
    setDormancyBannerDismissed(true);
  }, [currentMonthKey]);

  // Backfill nudge banner — shown once per user when their library has
  // plants with obvious empty care fields AND they've never run the backfill
  // flow. Points at Settings › Fill missing information. Dismissable and
  // sticky (localStorage), so we don't nag on every visit. Drive mode only —
  // local mode has no server-side backfill.
  const [backfillPromptDismissed, setBackfillPromptDismissed] = useState(
    () => localStorage.getItem('backfill_prompt_dismissed') === '1'
  );
  const dismissBackfillPrompt = useCallback(() => {
    localStorage.setItem('backfill_prompt_dismissed', '1');
    setBackfillPromptDismissed(true);
  }, []);

  // Recent searches — last 5 unique committed queries, newest first.
  // Persisted so users don't have to retype favourites every session.
  const RECENT_SEARCHES_KEY = 'plant_recent_searches_v1';
  const MAX_RECENT_SEARCHES = 5;
  const [recentSearches, setRecentSearches] = useState(() => {
    try {
      const raw = localStorage.getItem(RECENT_SEARCHES_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed.filter((s) => typeof s === 'string').slice(0, MAX_RECENT_SEARCHES) : [];
    } catch { return []; }
  });
  const [showRecentSearches, setShowRecentSearches] = useState(false);
  const searchContainerRef = useRef(null);
  const commitSearchToHistory = useCallback((raw) => {
    const q = (raw || '').trim();
    if (q.length < 2) return; // ignore very short queries — mostly noise
    setRecentSearches((prev) => {
      const deduped = [q, ...prev.filter((s) => s.toLowerCase() !== q.toLowerCase())].slice(0, MAX_RECENT_SEARCHES);
      try { localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(deduped)); } catch { /* quota */ }
      return deduped;
    });
  }, []);
  const removeRecentSearch = useCallback((q) => {
    setRecentSearches((prev) => {
      const next = prev.filter((s) => s !== q);
      try { localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(next)); } catch { /* quota */ }
      return next;
    });
  }, []);
  const clearRecentSearches = useCallback(() => {
    setRecentSearches([]);
    try { localStorage.removeItem(RECENT_SEARCHES_KEY); } catch { /* ignore */ }
    setShowRecentSearches(false);
  }, []);
  // Hide the dropdown when the user clicks/taps anywhere outside the search area.
  useEffect(() => {
    if (!showRecentSearches) return undefined;
    const handleDocClick = (e) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(e.target)) {
        setShowRecentSearches(false);
      }
    };
    document.addEventListener('mousedown', handleDocClick);
    document.addEventListener('touchstart', handleDocClick, { passive: true });
    return () => {
      document.removeEventListener('mousedown', handleDocClick);
      document.removeEventListener('touchstart', handleDocClick);
    };
  }, [showRecentSearches]);

  // First-run onboarding tour auto-trigger. Show when: (1) library has
  // finished loading, (2) it's empty (a returning user with plants doesn't
  // need the tour), (3) the user has never dismissed / completed it.
  // Runs once via a `once`-flag pattern so re-renders don't re-fire.
  useEffect(() => {
    if (loading) return;
    if (plants.length > 0) return;
    try {
      if (localStorage.getItem('plant_onboarding_tour_seen') === '1') return;
    } catch (_e) { /* SSR / privacy mode */ }
    // Small delay so the empty-state art has a moment to render first.
    const timer = setTimeout(() => setShowOnboardingTour(true), 800);
    return () => clearTimeout(timer);
  }, [loading, plants.length]);
  // Client-side "missing fields" heuristic — mirrors the backend's
  // `_missing_fields` closure but restricted to the fields the AI reliably
  // populates at plant-ID time. Dormancy + bloom fields are excluded
  // because they legitimately return null for evergreen foliage plants
  // (Pothos, ZZ, etc.), and we don't want to false-positive those into
  // the nudge count.
  const backfillCoreFields = useMemo(
    () => ['watering', 'light', 'humidity', 'temperature', 'toxicity', 'growth_rate', 'family', 'plant_type', 'environment'],
    []
  );
  const plantsNeedingBackfillCount = useMemo(() => {
    let n = 0;
    for (const p of plants) {
      if (p.archived || p.incomplete) continue;
      const care = p.care_details || {};
      if (backfillCoreFields.some((f) => !care[f])) n++;
    }
    return n;
  }, [plants, backfillCoreFields]);
  // Show only if: drive mode, at least one plant needs it, user has never
  // successfully run the backfill (no `plant_last_backfill` sentinel), and
  // the nudge hasn't been dismissed.
  const showBackfillPrompt = (
    !isLocalMode &&
    !backfillPromptDismissed &&
    plantsNeedingBackfillCount > 0 &&
    !localStorage.getItem('plant_last_backfill')
  );
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkSelected, setBulkSelected] = useState(new Set());
  const [bulkApplying, setBulkApplying] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [clearingIncomplete, setClearingIncomplete] = useState(false);
  const [archivingPlant, setArchivingPlant] = useState(null);
  const [enlargedPhoto, setEnlargedPhoto] = useState(null);
  const [showGroupedThumbs, setShowGroupedThumbs] = useState(() => localStorage.getItem('plant_grouped_thumbs') === 'true');
  const scrollYRef = useRef(0);
  const skipNextPushRef = useRef(false);

  const { fabRef, fabPos, handlePointerDown, handlePointerMove, handlePointerUp } =
    useDraggableFab(() => setShowAddModal(true));

  const openPlantDetail = useCallback((plant) => {
    scrollYRef.current = window.scrollY;
    setSelectedPlant(plant);
  }, []);

  const closePlantDetail = useCallback(() => {
    setSelectedPlant(null);
    setEnlargedPhoto(null);
    requestAnimationFrame(() => {
      window.scrollTo(0, scrollYRef.current);
    });
  }, []);

  // --- Browser back button / swipe-back integration ---
  // When a modal opens, push a history entry so that pressing back closes the modal
  // instead of navigating away (which would sign the user out on mobile).
  // 'enlarged' is the deepest layer (full-screen photo inside the plant detail modal).
  const activeModal = enlargedPhoto !== null ? 'enlarged'
    : selectedPlant ? 'detail'
    : showAddModal ? 'add'
    : editingPlant ? 'edit'
    : showSettingsModal ? 'settings'
    : qrInviteUrl ? 'qr-invite'
    : showLogoutConfirm ? 'logout-confirm'
    : null;

  // Ref populated by AddPlantModal so a back-swipe/back-press mid-batch
  // can prompt "are you sure you want to lose the remaining photos"
  // instead of silently dropping the queue. `{ pendingCount, isCropping }`
  // — set by AddPlantModal's useEffect and cleared on unmount.
  const addModalPendingRef = useRef({ pendingCount: 0, isCropping: false });
  // Branded AlertDialog for the batch-exit prompt. Replaces the native
  // window.confirm and adds an explicit "Save for later" option.
  const [batchExitDialog, setBatchExitDialog] = useState({ open: false, pendingCount: 0 });
  const [clearIncompleteOpen, setClearIncompleteOpen] = useState(false);

  useEffect(() => {
    if (skipNextPushRef.current) {
      // The state change was caused by popstate — don't push a redundant entry
      skipNextPushRef.current = false;
      return;
    }
    if (activeModal) {
      window.history.pushState({ modal: activeModal }, '');
    }
  }, [activeModal]);

  // PWA Share Target: if the user just shared a photo INTO our app via the
  // OS share sheet, the service worker (sw.js) stashed it and redirected
  // here with ?shared=1. Read the inbox, decode the base64 payload back
  // into a File, and open AddPlantModal pre-loaded.
  useEffect(() => {
    if (!location.search.includes('shared=1')) return;
    // Lazily create the pop-once promise. Both mounts (StrictMode dev) will
    // await the same result, so only one IDB pop happens per page load.
    if (!_shareTargetPromise) {
      _shareTargetPromise = (async () => {
        try {
          const { popSharedIncoming } = await import('../utils/shareTargetInbox');
          return await popSharedIncoming();
        } catch (err) {
          console.warn('Could not read shared inbox:', err);
          return null;
        }
      })();
    }
    let alive = true;
    _shareTargetPromise.then((entry) => {
      // Clean the URL so refresh doesn't retrigger.
      window.history.replaceState({}, '', '/library');
      if (!alive || !entry) return;
      try {
        if (entry.data_url) {
          // Decode the data-URL to a Blob without using fetch() — our CSP's
          // connect-src doesn't include the `data:` scheme, so fetch()
          // silently fails. Manual atob avoids that constraint entirely.
          const mime = entry.mime_type || 'image/jpeg';
          const b64 = entry.data_url.substring(entry.data_url.indexOf(',') + 1);
          const bin = atob(b64);
          const bytes = new Uint8Array(bin.length);
          for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
          const blob = new Blob([bytes], { type: mime });
          const ext = mime.split('/')[1] || 'jpg';
          const file = new File([blob], `shared-photo.${ext}`, { type: mime });
          setSharedInitialFile(file);
          setShowAddModal(true);
          toast.success('Shared photo received — identify or add manually');
        } else if (entry.text || entry.title || entry.url) {
          setShowAddModal(true);
          toast(`Shared text: "${(entry.text || entry.title || entry.url).slice(0, 60)}"`);
        }
      } catch (err) {
        console.warn('Could not process shared payload:', err);
      }
    });
    return () => { alive = false; };
  }, [location.search]);

  useEffect(() => {
    const handlePopState = (e) => {
      // Any state change we trigger from here was caused by popstate, so we
      // should NOT push a fresh history entry in response.
      skipNextPushRef.current = true;
      // Close the deepest open layer first
      if (enlargedPhoto !== null) {
        setEnlargedPhoto(null);
      } else if (selectedPlant) {
        closePlantDetail();
      } else if (showAddModal) {
        // Guard against silently losing an in-progress batch. If
        // AddPlantModal has told us there are queued photos remaining,
        // open the branded AlertDialog so the user can choose to save
        // for later, discard, or cancel. Re-push a history entry first
        // so the modal stays open until they pick an action.
        const info = addModalPendingRef.current || {};
        if (info.pendingCount > 0) {
          window.history.pushState({ modal: 'add' }, '');
          setBatchExitDialog({ open: true, pendingCount: info.pendingCount });
          return;
        }
        setShowAddModal(false);
      } else if (editingPlant) {
        setEditingPlant(null);
      } else if (showSettingsModal) {
        setShowSettingsModal(false);
      } else if (qrInviteUrl) {
        setQrInviteUrl(null);
      } else if (showLogoutConfirm) {
        // Back button on the logout confirm dialog cancels it
        setShowLogoutConfirm(false);
      } else {
        // No modal open — back-press at the library root: show "Sign out?"
        // confirm instead of silently re-pushing state. This gives users an
        // explicit way to switch accounts (especially mobile users who reach
        // for the back button by reflex).
        skipNextPushRef.current = false; // let the activeModal effect push state for the new dialog
        setShowLogoutConfirm(true);
      }
    };
    // Initial push to trap back when no modal is open
    window.history.pushState(null, '', '/library');
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [selectedPlant, showAddModal, editingPlant, showSettingsModal, enlargedPhoto, showLogoutConfirm, qrInviteUrl, closePlantDetail]);

  // --- Handlers that bridge hooks + modal state ---

  const handlePlantAdded = () => {
    onPlantAdded();
    setShowAddModal(false);
    setEditingPlant(null);
  };

  const handleEdit = (plant) => {
    setEditingPlant(plant);
    setShowAddModal(true);
  };

  const handleDeleteClick = (plantId) => {
    setShowAddModal(false);
    setEditingPlant(null);
    setPlantToDelete(plantId);
  };

  const confirmDelete = async () => {
    if (!plantToDelete) return;
    const ok = await onPlantDeleted(plantToDelete);
    setPlantToDelete(null);
    if (ok) closePlantDetail();
  };

  const handleShowQrCode = async () => {
    const active = plants.filter((p) => !p.archived);
    if (active.length === 0) {
      toast.error('No plants to share');
      return;
    }
    const sessionToken = localStorage.getItem('auth_hint');
    // Local-mode users have photos in IndexedDB; Drive-mode users have
    // them behind a session token. Either way `batchGetInviteThumbnails`
    // knows how to fetch — so both modes get real photos on the invite
    // page (was previously local-mode-blocked, which showed leaf
    // placeholders on friends' screens).
    const canEmbed = isLocalMode || !!sessionToken;
    const { buildInviteUrl, createShortInviteUrl } = await import('../utils/inviteLink');
    // Prefer the short `/g/<slug>` URL — always fits in a QR at high error
    // correction. Fall back to the fragment URL for local-mode users; we
    // still cap the length there so the QR renders reliably.
    let invite;
    if (canEmbed) {
      // Preload thumbnails so friends see plant photos on the invite page.
      const eligible = active.filter((p) => !p.incomplete);
      let thumbnailMap = null;
      if (eligible.length > 6) toast.loading(`Preparing invite… 0/${eligible.length}`, { id: 'invite-thumbs', position: 'bottom-center' });
      try {
        const { batchGetInviteThumbnails } = await import('../utils/inviteThumbs');
        thumbnailMap = await batchGetInviteThumbnails(eligible, {
          isLocalMode,
          sessionToken,
          onProgress: (done, total) => {
            if (eligible.length > 6) toast.loading(`Preparing invite… ${done}/${total}`, { id: 'invite-thumbs', position: 'bottom-center' });
          },
        });
      } catch (e) { /* ignore, invite still works without thumbs */ }
      toast.dismiss('invite-thumbs');
      invite = await createShortInviteUrl(active, {
        senderName: user?.name || null,
        viewState: { sortBy, thenBy },
        sessionToken,
        thumbnailMap,
      });
      // If the shortlink call fell back to a fragment URL (network hiccup),
      // it may be too long for a QR — re-run with the QR length cap.
      if (!invite.short && invite.url && invite.url.length > 2500) {
        invite = buildInviteUrl(active, {
          senderName: user?.name || null,
          viewState: { sortBy, thenBy },
          maxUrlLength: 2500,
        });
      }
    } else {
      invite = buildInviteUrl(active, {
        senderName: user?.name || null,
        viewState: { sortBy, thenBy },
        maxUrlLength: 2500,
      });
    }
    setQrInviteUrl({
      url: invite.url,
      includedCount: invite.includedCount,
      totalCount: invite.activeCount ?? invite.totalCount,
      truncated: !!invite.truncated,
      short: !!invite.short,
    });
  };

  const toggleGroupedThumbs = () => {
    setShowGroupedThumbs(v => {
      const next = !v;
      localStorage.setItem('plant_grouped_thumbs', String(next));
      return next;
    });
  };

  // Quick "copy share link" for a single plant — reuses the same public
  // short-link pipeline as the full Share flow, minus the dialog. Mirrors
  // the About page's per-section copy-link affordance.
  const handleCopyPlantLink = async (plant) => {
    try {
      const sessionToken = localStorage.getItem('auth_hint');
      const canEmbed = isLocalMode || !!sessionToken;
      const { createPlantShortInviteUrl } = await import('../utils/inviteLink');
      let thumbDataUrl = null;
      if (canEmbed) {
        try {
          const { batchGetInviteThumbnails } = await import('../utils/inviteThumbs');
          const map = await batchGetInviteThumbnails([plant], { isLocalMode, sessionToken });
          if (map && plant.plant_id && map.has(plant.plant_id)) thumbDataUrl = map.get(plant.plant_id);
        } catch { /* non-blocking */ }
      }
      const invite = await createPlantShortInviteUrl(plant, {
        senderName: user?.name || null,
        sessionToken,
        thumbDataUrl,
      });
      await navigator.clipboard.writeText(invite.url);
      toast.success(t('linkCopied') || 'Link copied');
    } catch (e) {
      toast.error(t('copyLinkFailed') || 'Could not copy link');
    }
  };

  // Show an on-screen scannable QR code for a single plant's invite link —
  // reuses the same public short-link pipeline as copy/share, then hands the
  // URL to InviteQrModal (with a plant-specific title).
  const handleShowPlantQr = async (plant) => {
    try {
      const sessionToken = localStorage.getItem('auth_hint');
      const canEmbed = isLocalMode || !!sessionToken;
      const { createPlantShortInviteUrl } = await import('../utils/inviteLink');
      let thumbDataUrl = null;
      if (canEmbed) {
        try {
          const { batchGetInviteThumbnails } = await import('../utils/inviteThumbs');
          const map = await batchGetInviteThumbnails([plant], { isLocalMode, sessionToken });
          if (map && plant.plant_id && map.has(plant.plant_id)) thumbDataUrl = map.get(plant.plant_id);
        } catch { /* non-blocking */ }
      }
      const invite = await createPlantShortInviteUrl(plant, {
        senderName: user?.name || null,
        sessionToken,
        thumbDataUrl,
      });
      setQrInviteUrl({ url: invite.url, title: t('qrScanPlant') || 'Scan to open this plant' });
    } catch (e) {
      toast.error(t('qrCreateFailed') || 'Could not create QR code');
    }
  };

  // Compact action cluster for the text-only list rows (List + No photos),
  // matching the PlantCard list view: copy share link, edit, delete.
  const renderRowActions = (plant) => (
    <div className="flex items-center gap-0.5 flex-shrink-0" data-testid={`list-row-actions-${plant.plant_id}`}>
      {!plant.incomplete && (
        <button
          onClick={(e) => { e.stopPropagation(); handleCopyPlantLink(plant); }}
          className="p-1.5 rounded-full hover:bg-gray-100 transition-colors"
          aria-label={`${t('copyPlantLink') || 'Copy share link'} ${plant.common_name || 'plant'}`}
          title={t('copyPlantLink') || 'Copy share link'}
          data-testid={`list-copy-link-${plant.plant_id}`}
        >
          <Link2 className="w-3.5 h-3.5" style={{ color: 'hsl(143, 28%, 45%)' }} />
        </button>
      )}
      <button
        onClick={(e) => { e.stopPropagation(); handleEdit(plant); }}
        className="p-1.5 rounded-full hover:bg-gray-100 transition-colors"
        aria-label={`${t('editPlant') || 'Edit'} ${plant.common_name || 'plant'}`}
        title={t('editPlant') || 'Edit'}
        data-testid={`list-edit-${plant.plant_id}`}
      >
        <PencilLine className="w-3.5 h-3.5" style={{ color: 'hsl(143, 28%, 25%)' }} />
      </button>
      <button
        onClick={(e) => { e.stopPropagation(); handleDeleteClick(plant.plant_id); }}
        className="p-1.5 rounded-full hover:bg-gray-100 transition-colors"
        aria-label={`${t('delete') || 'Delete'} ${plant.common_name || 'plant'}`}
        title={t('delete') || 'Delete'}
        data-testid={`list-delete-${plant.plant_id}`}
      >
        <Trash2 className="w-3.5 h-3.5" style={{ color: 'hsl(0, 70%, 50%)' }} />
      </button>
    </div>
  );

  // Central handler used by ShareChoiceModal. `choice` selects the share
  // variant; `format` picks image vs text. This is the ONE place that
  // knows how to build the correct invite URL for each option and hand
  // it off to the right renderer in `utils/shareCards.js`.
  const handleSharePicked = async ({ choice, format, plant: pickedPlant, note = null, onProgress = null }) => {
    const active = plants.filter((p) => !p.archived && !p.incomplete);
    if (choice !== 'plant-full' && active.length === 0) {
      toast.error('No plants to share');
      return;
    }
    const sessionToken = localStorage.getItem('auth_hint');
    // Thumbnails: local mode reads them from IndexedDB; Drive mode
    // needs a session token. Either way `batchGetInviteThumbnails` can
    // handle it — so both modes now get real photos in the invite
    // payload (previously local-mode was blocked, and friends saw
    // leaf placeholders).
    const canEmbed = isLocalMode || !!sessionToken;
    const { sharePlantSingle } = await import('../utils/shareCards');
    if (choice === 'plant-full') {
      if (!pickedPlant) { toast.error('No plant selected'); return; }
      const { createPlantShortInviteUrl } = await import('../utils/inviteLink');
      // Try to pre-fetch this plant's thumbnail so the shareable page
      // renders with a real photo instead of a leaf placeholder.
      let thumbDataUrl = null;
      if (canEmbed) {
        try {
          const { batchGetInviteThumbnails } = await import('../utils/inviteThumbs');
          const map = await batchGetInviteThumbnails([pickedPlant], { isLocalMode, sessionToken });
          if (map && pickedPlant.plant_id && map.has(pickedPlant.plant_id)) {
            thumbDataUrl = map.get(pickedPlant.plant_id);
          }
        } catch { /* non-blocking */ }
      }
      const invite = await createPlantShortInviteUrl(pickedPlant, {
        senderName: user?.name || null,
        note,
        sessionToken,
        thumbDataUrl,
      });
      const result = await sharePlantSingle(pickedPlant, {
        inviteUrl: invite.url,
        senderName: user?.name || null,
        isLocalMode,
        sessionToken,
      });
      if (result?.shared) toast.success('Shared!');
      else if (result?.downloaded) toast.success('Image downloaded — post it anywhere');
      else if (result?.copied) toast.success('Message copied to clipboard');
      return;
    }

    // Library-level shares (Options 2 & 3) — always try the shortlink
    // endpoint. Falls back to a fragment URL internally on failure.
    const { createShortInviteUrl } = await import('../utils/inviteLink');
    const eligible = active.filter((p) => !p.incomplete);
    let thumbnailMap = null;
    if (canEmbed && eligible.length > 6) toast.loading(`Preparing invite… 0/${eligible.length}`, { id: 'invite-thumbs', position: 'bottom-center' });
    if (canEmbed) {
      try {
        const { batchGetInviteThumbnails } = await import('../utils/inviteThumbs');
        thumbnailMap = await batchGetInviteThumbnails(eligible, {
          isLocalMode,
          sessionToken,
          onProgress: (done, total) => {
            if (eligible.length > 6) toast.loading(`Preparing invite… ${done}/${total}`, { id: 'invite-thumbs', position: 'bottom-center' });
          },
        });
      } catch { /* non-blocking */ }
    }
    toast.dismiss('invite-thumbs');
    const invite = await createShortInviteUrl(active, { senderName: user?.name || null, note, viewState: { sortBy, thenBy }, sessionToken, thumbnailMap });
    const { shareLibraryPdf } = await import('../utils/shareLibraryPdf');
    const mode = choice === 'library-minimal' ? 'minimal' : choice === 'library-full' ? 'full' : 'names-only';
    if (mode === 'full') toast.loading('Building full PDF…', { id: 'building-pdf', position: 'bottom-center' });
    const result = await shareLibraryPdf(active, {
      inviteUrl: invite.url,
      mode,
      senderName: user?.name || null,
      note,
      isLocalMode,
      sessionToken,
      thumbnailMap,
      totalCount: invite.activeCount ?? active.length,
      truncated: !!invite.truncated,
      includedCount: invite.includedCount ?? active.length,
      onProgress: (done, total) => {
        if (mode === 'full') {
          toast.loading(`Building PDF… ${done}/${total}`, { id: 'building-pdf', position: 'bottom-center' });
          onProgress?.(done, total);
        }
      },
    });
    toast.dismiss('building-pdf');
    if (result?.shared) toast.success('Shared!');
    else if (result?.downloaded) toast.success('PDF downloaded — share it anywhere');
    else if (result?.copied) toast.success('Message copied to clipboard');
  };

  // Persistent record of thumbnails we've already warmed for THIS user so later
  // sessions re-warm ONLY newly added plants (brand-new photos stay instant)
  // instead of re-fetching the whole library every time. A replaced photo gets
  // a NEW drive id, so it's naturally treated as fresh. Capped so localStorage
  // can't grow unbounded.
  const warmedIdsKey = useCallback(() => `warmed_ids_${user?.user_id || 'me'}`, [user?.user_id]);
  const readWarmedIds = useCallback(() => {
    try { return new Set(JSON.parse(localStorage.getItem(warmedIdsKey()) || '[]')); }
    catch { return new Set(); }
  }, [warmedIdsKey]);
  const addWarmedIds = useCallback((ids) => {
    if (!ids || !ids.length) return;
    try {
      const s = readWarmedIds();
      ids.forEach((id) => s.add(id));
      localStorage.setItem(warmedIdsKey(), JSON.stringify(Array.from(s).slice(-5000)));
    } catch { /* quota / unavailable */ }
  }, [readWarmedIds, warmedIdsKey]);

  // One-tap "Preload all photos": pre-fetches every plant's thumbnail so the
  // backend's durable cache (Mongo + disk) is warm. Makes the first library
  // scroll AND the next PDF share instant on huge Drive libraries, which
  // otherwise pay a slow cold-cache Drive round-trip per photo. Drive-mode
  // only — local photos already live on the device.
  const handleWarmPhotos = async () => {
    if (warmingPhotos) return;
    if (isLocalMode) {
      toast.info(t('photosOnDevice') || 'Your photos are already on this device');
      return;
    }
    const sessionToken = localStorage.getItem('auth_hint');
    const withPhotos = plants.filter((p) => !p.archived && p.photo_drive_id);
    if (withPhotos.length === 0) {
      toast.info(t('noPhotosToPreload') || 'No photos to preload');
      return;
    }
    setWarmingPhotos(true);
    const total = withPhotos.length;
    let done = 0;
    let failed = 0;
    const okIds = [];
    const label = () => `${t('preloadingPhotos') || 'Preloading photos…'} ${done}/${total}`;
    toast.loading(label(), { id: 'warm-photos', position: 'bottom-center' });
    const queue = withPhotos.slice();
    const worker = async () => {
      while (queue.length) {
        const p = queue.shift();
        try {
          await axios.get(`${BACKEND_URL}/api/photos/${p.photo_drive_id}?thumbnail=true`, {
            headers: { Authorization: `Bearer ${sessionToken}` },
            responseType: 'blob',
            timeout: 30000,
          });
          okIds.push(p.photo_drive_id);
        } catch {
          failed += 1;
        }
        done += 1;
        toast.loading(label(), { id: 'warm-photos', position: 'bottom-center' });
      }
    };
    try {
      await Promise.all(Array.from({ length: Math.min(6, total) }, worker));
    } finally {
      toast.dismiss('warm-photos');
      setWarmingPhotos(false);
    }
    addWarmedIds(okIds); // keep the auto-warmer from redoing these next session
    const ok = total - failed;
    if (failed === 0) {
      toast.success(`${t('photosReady') || 'Photos ready'} — ${ok}/${total}`);
    } else {
      toast.success(`${t('photosReady') || 'Photos ready'} — ${ok}/${total} (${failed} ${t('couldNotLoad') || 'unavailable'})`);
    }
  };

  // Auto warm-up: quietly pre-fetch thumbnails in the background a few seconds
  // after the library loads (Drive mode only), at low concurrency so it never
  // competes with the visible grid. Warms ONLY plants not already warmed for
  // this user — so the first-ever session warms everything, and later sessions
  // instantly warm just the plants you've added since. Photo responses are
  // immutable-cached, so this is cheap on repeat.
  const plantsRef = useRef(plants);
  plantsRef.current = plants;
  const autoWarmStartedRef = useRef(false);
  const hasWarmablePhotos = !isLocalMode && plants.some((p) => !p.archived && p.photo_drive_id);
  useEffect(() => {
    if (!hasWarmablePhotos || autoWarmStartedRef.current) return undefined;
    const sessionToken = localStorage.getItem('auth_hint');
    if (!sessionToken) return undefined;
    autoWarmStartedRef.current = true;
    let cancelled = false;
    const timer = setTimeout(async () => {
      const warmed = readWarmedIds();
      const withPhotos = (plantsRef.current || []).filter(
        (p) => !p.archived && p.photo_drive_id && !warmed.has(p.photo_drive_id)
      );
      if (withPhotos.length === 0) return; // nothing new to warm
      const queue = withPhotos.slice();
      const okIds = [];
      const worker = async () => {
        while (!cancelled && queue.length) {
          const p = queue.shift();
          try {
            await axios.get(`${BACKEND_URL}/api/photos/${p.photo_drive_id}?thumbnail=true`, {
              headers: { Authorization: `Bearer ${sessionToken}` },
              responseType: 'blob',
              timeout: 30000,
            });
            okIds.push(p.photo_drive_id);
          } catch { /* ignore individual failures */ }
        }
      };
      await Promise.all(Array.from({ length: Math.min(3, queue.length) }, worker));
      if (!cancelled) addWarmedIds(okIds);
    }, 4000);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [hasWarmablePhotos, isLocalMode, user?.user_id, readWarmedIds, addWarmedIds]);



  const handleArchiveFromCard = (plant) => {
    setArchivingPlant(plant);
  };

  const confirmArchive = async (reason) => {
    if (!archivingPlant) return;
    try {
      let queued = false;
      if (isLocalMode) {
        await localStore.archivePlant(archivingPlant.plant_id, reason);
      } else {
        const sessionToken = localStorage.getItem('auth_hint');
        const resp = await axios.post(`${BACKEND_URL}/api/plants/${archivingPlant.plant_id}/archive`, { reason }, {
          headers: { Authorization: `Bearer ${sessionToken}` }
        });
        queued = !!resp?.data?.queued;
      }
      if (!queued) toast.success('Plant archived');
      setArchivingPlant(null);
      onPlantAdded();
    } catch (error) {
      console.error('Failed to archive plant:', error);
      toast.error('Failed to archive plant');
    }
  };

  const handleLogout = async () => {
    if (isLocalMode) {
      exitLocalMode();
      navigate('/login', { replace: true });
      return;
    }
    try {
      const sessionToken = localStorage.getItem('auth_hint');
      await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
    } catch (err) {
      // Logout endpoint failure is non-blocking — we still clear local state
      console.debug('Logout API call failed (continuing local logout):', err?.message);
    }
    // Signal that the next login should force the Google account picker (so
    // a returning user can switch accounts instead of being auto-signed back
    // into the previous one).
    sessionStorage.setItem('force_account_picker', 'true');
    authLogout();
    navigate('/login', { replace: true });
  };

  // Count plants missing family/type
  const plantsNeedingBackfill = plants.filter(p =>
    p.common_name && (!p.care_details?.family || !p.care_details?.plant_type)
  );

  // Count for the terrarium filter pill — drawn from the full active library
  // (excluding archived + incomplete) so the number doesn't shift around as
  // the user toggles search or other controls.
  const terrariumCount = useMemo(() => {
    let n = 0;
    for (const p of plants) {
      if (p.archived || p.incomplete) continue;
      const env = (p.care_details?.environment || '').toLowerCase();
      if (env.includes('terrarium') || env.includes('conservatory')) n++;
    }
    return n;
  }, [plants]);

  // Count of plants currently in their dormancy window (based on today's month).
  // Excludes archived + incomplete so the banner + chip only reflect plants
  // the owner actually needs to think about.
  const dormantNowCount = useMemo(() => {
    const currentMonth = new Date().getMonth() + 1;
    let n = 0;
    for (const p of plants) {
      if (p.archived || p.incomplete) continue;
      const months = p.care_details?.dormancy_months;
      if (Array.isArray(months) && months.includes(currentMonth)) n++;
    }
    return n;
  }, [plants]);

  // Count of plants that REQUIRE a dormancy period (regardless of whether
  // today is inside that window). Powers the always-visible "Needs dormancy"
  // filter chip so users can find their seasonal-care plants year-round.
  const needsDormancyCount = useMemo(() => {
    let n = 0;
    for (const p of plants) {
      if (p.archived || p.incomplete) continue;
      const months = p.care_details?.dormancy_months;
      if (Array.isArray(months) && months.length > 0) n++;
    }
    return n;
  }, [plants]);

  const runBackfill = async () => {
    setBackfilling(true);
    let updated = 0;
    try {
      if (isLocalMode) {
        // Local mode: call anonymous endpoint per plant, update IndexedDB
        for (const plant of plantsNeedingBackfill) {
          try {
            const name = plant.scientific_name || plant.common_name;
            const resp = await axios.post(`${BACKEND_URL}/api/plants/backfill-family-type-anonymous`, { plant_name: name });
            const data = resp.data;
            const care = { ...(plant.care_details || {}) };
            if (data.family && !care.family) care.family = data.family;
            if (data.plant_type && !care.plant_type) care.plant_type = data.plant_type;
            await localStore.updatePlant(plant.plant_id, { care_details: care });
            updated++;
          } catch (e) {
            console.warn(`Backfill failed for ${plant.common_name}:`, e);
          }
        }
      } else {
        // Authenticated: call bulk endpoint
        const sessionToken = localStorage.getItem('auth_hint');
        const resp = await axios.post(`${BACKEND_URL}/api/plants/backfill-family-type`, {}, {
          headers: { Authorization: `Bearer ${sessionToken}` },
          timeout: 120000
        });
        updated = resp.data.updated || 0;
      }
      toast.success(`Updated ${updated} plant${updated !== 1 ? 's' : ''} with Family & Type info`);
      onPlantAdded(); // refresh list
    } catch (e) {
      toast.error('Failed to update plants: ' + (e.response?.data?.detail || e.message));
    } finally {
      setBackfilling(false);
    }
  };

  // --- Bulk edit handlers ---
  const toggleBulkSelect = (plantId) => {
    setBulkSelected(prev => {
      const next = new Set(prev);
      if (next.has(plantId)) next.delete(plantId);
      else next.add(plantId);
      return next;
    });
  };

  const bulkSelectAll = () => {
    const completePlants = filteredPlants.filter(p => !p.incomplete);
    setBulkSelected(new Set(completePlants.map(p => p.plant_id)));
  };

  const bulkDeselectAll = () => setBulkSelected(new Set());

  const handleBulkApply = async (field, value) => {
    const ids = [...bulkSelected];
    if (ids.length === 0) return;
    setBulkApplying(true);
    try {
      if (isLocalMode) {
        for (const plantId of ids) {
          const plant = plants.find(p => p.plant_id === plantId);
          if (plant) {
            const care = { ...(plant.care_details || {}), [field]: field === 'family' || field === 'plant_type' ? value : value.toLowerCase() };
            await localStore.updatePlant(plantId, { care_details: care });
          }
        }
        toast.success(t('bulkUpdateSuccess').replace('{count}', ids.length));
      } else {
        const sessionToken = localStorage.getItem('auth_hint');
        const resp = await axios.post(`${BACKEND_URL}/api/plants/bulk-update`, {
          plant_ids: ids,
          field,
          value
        }, { headers: { Authorization: `Bearer ${sessionToken}` } });
        if (!resp.data?.queued) toast.success(t('bulkUpdateSuccess').replace('{count}', resp.data.updated));
      }
      onPlantAdded(); // refresh
      setBulkMode(false);
      setBulkSelected(new Set());
    } catch (e) {
      toast.error(t('bulkUpdateFailed') + ': ' + (e.response?.data?.detail || e.message));
    } finally {
      setBulkApplying(false);
    }
  };

  const handleBulkDelete = async () => {
    const ids = [...bulkSelected];
    if (ids.length === 0) return;
    setBulkDeleting(true);
    try {
      if (isLocalMode) {
        for (const plantId of ids) {
          await localStore.deletePlant(plantId);
        }
        toast.success(t('bulkDeleteSuccess').replace('{count}', ids.length));
      } else {
        const sessionToken = localStorage.getItem('auth_hint');
        const resp = await axios.post(`${BACKEND_URL}/api/plants/bulk-delete`, {
          plant_ids: ids,
        }, { headers: { Authorization: `Bearer ${sessionToken}` } });
        if (!resp.data?.queued) toast.success(t('bulkDeleteSuccess').replace('{count}', resp.data.deleted));
      }
      onPlantAdded(); // refresh
      setBulkMode(false);
      setBulkSelected(new Set());
    } catch (e) {
      toast.error(t('bulkDeleteFailed') + ': ' + (e.response?.data?.detail || e.message));
    } finally {
      setBulkDeleting(false);
    }
  };

  const cancelBulkMode = () => {
    setBulkMode(false);
    setBulkSelected(new Set());
  };

  // Bulk-remove all unfinished (incomplete) entries — cleans up drafts left
  // behind by earlier failed/abandoned batch adds in one tap.
  const handleClearIncomplete = () => {
    if (incompletePlants.length === 0 || clearingIncomplete) return;
    setClearIncompleteOpen(true);
  };

  const confirmClearIncomplete = async () => {
    const ids = incompletePlants.map((p) => p.plant_id);
    setClearIncompleteOpen(false);
    if (ids.length === 0) return;
    setClearingIncomplete(true);
    try {
      if (isLocalMode) {
        for (const id of ids) await localStore.deletePlant(id);
      } else {
        const sessionToken = localStorage.getItem('auth_hint');
        await axios.post(`${BACKEND_URL}/api/plants/bulk-delete`, { plant_ids: ids }, {
          headers: { Authorization: `Bearer ${sessionToken}` },
        });
      }
      toast.success((t('clearedUnfinished') || 'Removed {n} unfinished').replace('{n}', ids.length));
      onPlantAdded();
    } catch (e) {
      toast.error((t('bulkDeleteFailed') || 'Failed to remove') + ': ' + (e.response?.data?.detail || e.message));
    } finally {
      setClearingIncomplete(false);
    }
  };

  // --- Render gates ---

  if (checkingDrive) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="checking-drive">
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: 'hsl(143, 28%, 25%)' }} />
      </div>
    );
  }

  if (!driveConnected) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: 'hsl(82, 55%, 95%)' }}>
        <div className="max-w-md w-full text-center">
          <div className="rounded-3xl p-8 shadow-lg" style={{ backgroundColor: 'white' }}>
            <Sprout className="w-16 h-16 mx-auto mb-4" style={{ color: 'hsl(143, 28%, 25%)' }} />
            <h2 className="text-2xl font-display font-bold mb-4" style={{ color: 'hsl(143, 28%, 25%)' }}>
              {t('connectGoogleDrive')}
            </h2>
            <p className="mb-6" style={{ color: 'hsl(125, 12%, 32%)' }}>
              {t('connectDriveDesc')}
            </p>
            <Button
              onClick={handleConnectDrive}
              className="w-full rounded-full h-12 text-base font-medium"
              style={{ backgroundColor: 'hsl(143, 28%, 25%)', color: 'hsl(115, 56%, 96%)' }}
              data-testid="connect-drive-button"
            >
              {t('connectGoogleDrive')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-24" style={{ backgroundColor: 'hsl(82, 55%, 97%)' }}>
      {/* Draggable floating backfill progress chip — rendered at page root
          so it can be freely positioned anywhere on screen. Drive-mode
          only; the component self-hides when no job is running. */}
      {!isLocalMode && (
        <BackfillNotifierChip
          isLocalMode={isLocalMode}
          onOpenSettings={() => setShowSettingsModal(true)}
        />
      )}

      {/* Skip to content */}
      <a href="#plant-library-content" className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:top-2 focus:left-2 focus:px-4 focus:py-2 focus:bg-white focus:rounded-lg focus:shadow-lg focus:text-sm" style={{ color: 'hsl(143, 28%, 25%)' }} data-testid="skip-to-content">
        {t('skipToLibrary')}
      </a>

      {/* Language auto-detect chip — appears once on first visit if we
          picked a non-English language from navigator.language. One tap
          switches back to English; dismiss just hides the chip without
          changing the language. Both routes clear the flag so it never
          nags again. */}
      {autoDetected && autoDetectedLangName && (
        <div
          role="status"
          aria-live="polite"
          className="flex items-center justify-center gap-2 px-4 py-2 text-sm flex-wrap"
          style={{ backgroundColor: 'hsl(210, 70%, 96%)', color: 'hsl(210, 50%, 25%)' }}
          data-testid="lang-autodetect-chip"
        >
          <Globe className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
          <button
            type="button"
            onClick={switchBackToEnglish}
            className="underline font-medium hover:opacity-80 transition-opacity"
            style={{ color: 'hsl(210, 55%, 30%)' }}
            data-testid="lang-autodetect-switch"
          >
            {(t('langAutoDetectedChip') || 'Language set to {language} \u2014 tap to switch back to English').replace('{language}', autoDetectedLangName)}
          </button>
          <button
            type="button"
            onClick={dismissAutoDetected}
            className="flex-shrink-0 p-0.5 rounded hover:bg-white/60 transition-colors ml-1"
            style={{ color: 'hsl(210, 30%, 45%)' }}
            aria-label={t('langAutoDetectedChipDismiss') || 'Dismiss language chip'}
            data-testid="lang-autodetect-dismiss"
          >
            <XIcon className="w-4 h-4" aria-hidden="true" />
          </button>
        </div>
      )}

      {/* Offline banner — read-only browsing mode. Renders whenever the
          browser reports offline AND we have a Drive-mode session (in
          local mode users are always fully offline-capable so no need
          to surface this). */}
      {!isOnline && !isLocalMode && (
        <div
          role="status"
          aria-live="polite"
          className="flex items-center justify-center gap-2 px-4 py-2 text-sm flex-wrap"
          style={{ backgroundColor: 'hsl(210, 40%, 92%)', color: 'hsl(210, 30%, 25%)' }}
          data-testid="offline-banner"
        >
          <HardDrive className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
          <span>{t('offlineBanner') || 'You\u2019re offline \u2014 browsing your last saved library'}</span>
        </div>
      )}

      {/* Pending offline changes pill — tap to see the list + Retry now.
          Shown online or offline whenever the queue is non-empty. */}
      {!isLocalMode && pendingSyncCount > 0 && (
        <div className="flex items-center justify-center px-4 py-2">
          <PendingSyncPill
            pending={pendingSync}
            pendingCount={pendingSyncCount}
            flushing={pendingFlushing}
            onFlush={flushPendingSync}
            onDiscard={discardPendingSync}
            onDiscardAll={discardAllPendingSync}
            isOnline={isOnline}
            progress={pendingProgress}
            open={pendingDialogOpen}
            onOpenChange={setPendingDialogOpen}
          />
        </div>
      )}

      {/* Brief "all synced" confirmation after a flush empties the queue. */}
      {!isLocalMode && pendingJustSynced && pendingSyncCount === 0 && (
        <div className="flex items-center justify-center px-4 py-2">
          <span
            className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium"
            style={{ backgroundColor: 'hsl(143, 45%, 92%)', color: 'hsl(143, 45%, 26%)' }}
            data-testid="all-synced-chip"
          >
            <Check className="w-3.5 h-3.5" aria-hidden="true" />
            {t('allChangesSynced') || 'All changes synced'}
          </span>
        </div>
      )}


      {isLocalMode && (
        <div role="status" className="flex items-center justify-center gap-2 px-4 py-2 text-sm flex-wrap" style={{ backgroundColor: 'hsl(40, 90%, 92%)', color: 'hsl(40, 50%, 25%)' }} data-testid="local-mode-banner">
          <HardDrive className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
          <span>{t('deviceOnlyMode')} — {localPlantTotal} / {localStore.MAX_PLANTS} {t('plantsUsed')}</span>
          <span style={{ color: 'hsl(40, 40%, 60%)' }} aria-hidden="true">|</span>
          <button
            onClick={() => {
              exitLocalMode();
              const redirectUrl = window.location.origin + '/library';
              window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
            }}
            className="underline font-medium hover:opacity-80 transition-opacity"
            style={{ color: 'hsl(143, 35%, 30%)' }}
            data-testid="local-mode-sign-in-btn"
          >
            {t('signInGoogleSync')}
          </button>
        </div>
      )}

      {/* Header */}
      <header className="sticky top-0 z-40 glassmorphism border-b" style={{ borderColor: 'hsl(143, 28%, 90%)' }} role="banner">
        <div className="container mx-auto px-3 sm:px-4 py-3 sm:py-4">
          <div className="flex items-center justify-between gap-2 sm:gap-4">
            <div className="flex items-center gap-2 sm:gap-3 cursor-pointer min-w-0" onClick={() => window.location.href = '/library'} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); window.location.href = '/library'; }}} role="link" tabIndex={0} aria-label="Go to main library page" data-testid="app-home-link">
              <Sprout className="w-7 h-7 sm:w-8 sm:h-8 flex-shrink-0" style={{ color: 'hsl(143, 28%, 25%)' }} aria-hidden="true" />
              <h1 className="text-xl sm:text-2xl md:text-3xl font-display font-bold whitespace-nowrap" style={{ color: 'hsl(143, 28%, 25%)' }}>
                {t('myPlants')}
              </h1>
            </div>
            <div className="flex items-center gap-0.5 sm:gap-2 flex-shrink-0">
              <Button
                variant={bulkMode ? "default" : "ghost"}
                size="icon"
                onClick={() => bulkMode ? cancelBulkMode() : setBulkMode(true)}
                aria-label={t('bulkEdit')}
                title={t('bulkEdit')}
                style={bulkMode ? { backgroundColor: 'hsl(143, 28%, 25%)', color: 'white' } : undefined}
                data-testid="bulk-edit-toggle"
              >
                <PencilLine className="w-5 h-5" />
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" disabled={exporting} aria-label={t('sharePlants') || 'Share'} title={t('sharePlants') || 'Share'} data-testid="export-menu-button">
                    {exporting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Share2 className="w-5 h-5" />}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" data-testid="export-menu">
                  <DropdownMenuItem onClick={() => { setShareChoicePlant(null); setShareChoiceOpen(true); }} data-testid="share-choice-item">
                    <Share2 className="w-4 h-4 mr-2" aria-hidden="true" />
                    {t('sharePlants') || 'Share…'}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleCopyInvite({ sortBy, thenBy })} data-testid="copy-invite-item">
                    <Link2 className="w-4 h-4 mr-2" aria-hidden="true" />
                    {t('copyInviteLink') || 'Copy invite link'}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handlePreviewInvite({ sortBy, thenBy })} data-testid="preview-invite-item">
                    <Eye className="w-4 h-4 mr-2" aria-hidden="true" />
                    {t('previewInvite') || 'Preview invite page'}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleShowQrCode} data-testid="show-qr-item">
                    <QrCode className="w-4 h-4 mr-2" aria-hidden="true" />
                    {t('showQrCode') || 'Show QR code'}
                  </DropdownMenuItem>
                  {!isLocalMode && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={handleWarmPhotos}
                        disabled={warmingPhotos}
                        data-testid="warm-photos-item"
                      >
                        <ImageIcon className="w-4 h-4 mr-2" aria-hidden="true" />
                        {t('preloadPhotos') || 'Preload all photos'}
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
              {!isLocalMode && pendingSyncCount > 0 && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setPendingDialogOpen(true)}
                  aria-label={pendingSyncCount === 1 ? (t('pendingSyncPillOne') || '1 change pending sync') : (t('pendingSyncPill') || '{n} changes pending sync').replace('{n}', String(pendingSyncCount))}
                  title={t('pendingSyncTitle') || 'Changes waiting to sync'}
                  data-testid="pending-sync-badge-button"
                  className="relative"
                >
                  {pendingFlushing
                    ? <Loader2 className="w-5 h-5 animate-spin" style={{ color: 'hsl(35, 60%, 40%)' }} />
                    : <CloudOff className="w-5 h-5" style={{ color: 'hsl(35, 60%, 40%)' }} />}
                  <span
                    className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full text-[10px] font-bold flex items-center justify-center"
                    style={{ backgroundColor: 'hsl(30, 70%, 32%)', color: 'white' }}
                    data-testid="pending-sync-badge-count"
                  >
                    {pendingSyncCount}
                  </span>
                </Button>
              )}
              <Button variant="ghost" size="icon" onClick={() => navigate('/archived')} aria-label="View archived plants" title="Archived plants" data-testid="archive-page-button">
                <Archive className="w-5 h-5" />
              </Button>
              <Button variant="ghost" size="icon" onClick={() => setShowSettingsModal(true)} aria-label="Settings" title="Settings" data-testid="settings-button">
                <SettingsIcon className="w-5 h-5" />
              </Button>
              <Button variant="ghost" size="icon" onClick={() => setShowLogoutConfirm(true)} title={isLocalMode ? 'Exit local mode' : 'Logout'} data-testid="logout-button">
                <LogOut className="w-5 h-5" />
              </Button>
            </div>
          </div>

          {/* Search Bar */}
          <div className="mt-4 relative" ref={searchContainerRef}>
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 z-10 pointer-events-none" style={{ color: 'hsl(125, 12%, 32%)' }} />
            <form
              role="search"
              onSubmit={(e) => {
                // Wrapping the search input in a form is the standard way
                // to make iOS Safari's "Search" keyboard arrow commit the
                // input and dismiss the keyboard. Without this, tapping the
                // arrow may not fire a keydown at all on some iOS versions.
                e.preventDefault();
                const el = e.currentTarget.querySelector('input[data-testid="search-input"]');
                if (el && typeof el.blur === 'function') el.blur();
                commitSearchToHistory(searchQuery);
                setShowRecentSearches(false);
              }}
            >
              <Input
                type="search"
                placeholder={t('searchPlants')}
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  // Hide the dropdown once the user starts typing so it
                  // doesn't cover the filtered results.
                  if (e.target.value) setShowRecentSearches(false);
                  else setShowRecentSearches(recentSearches.length > 0);
                }}
                onFocus={() => {
                  // Open the dropdown only when the input is empty AND
                  // we have something to show — otherwise it's just noise.
                  if (!searchQuery && recentSearches.length > 0) setShowRecentSearches(true);
                }}
                onKeyDown={(e) => {
                  // Belt-and-braces: dismiss the keyboard on Enter for
                  // browsers that fire keydown before the form submit.
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    e.currentTarget.blur();
                    commitSearchToHistory(searchQuery);
                    setShowRecentSearches(false);
                  } else if (e.key === 'Escape') {
                    setShowRecentSearches(false);
                  }
                }}
                enterKeyHint="search"
                className="pl-12 pr-4 py-6 rounded-full text-base shadow-sm w-full"
                style={{ backgroundColor: 'rgba(255, 255, 255, 0.8)', borderColor: 'hsl(143, 28%, 85%)' }}
                aria-label="Search plants by name, scientific name, or family"
                role="combobox"
                aria-expanded={showRecentSearches}
                aria-controls="recent-searches-panel"
                aria-autocomplete="list"
                data-testid="search-input"
              />
            </form>

            {/* Recent searches dropdown — appears when the input is focused
                AND empty AND we have prior queries to offer. Tap a chip to
                refill the input; tap × to forget one; tap Clear to wipe all. */}
            {showRecentSearches && recentSearches.length > 0 && (
              <div
                id="recent-searches-panel"
                role="listbox"
                aria-label={t('recentSearchesLabel') || 'Recent searches'}
                className="absolute left-0 right-0 top-full mt-2 rounded-2xl shadow-lg border py-2 z-40"
                style={{
                  backgroundColor: 'white',
                  borderColor: 'hsl(143, 28%, 88%)',
                }}
                data-testid="recent-searches-panel"
              >
                <div
                  className="flex items-center justify-between px-4 pb-1.5 text-xs font-semibold uppercase tracking-wide"
                  style={{ color: 'hsl(125, 12%, 40%)' }}
                >
                  <span className="inline-flex items-center gap-1.5">
                    <Clock className="w-3.5 h-3.5" aria-hidden="true" />
                    {t('recentSearchesLabel') || 'Recent searches'}
                  </span>
                  <button
                    type="button"
                    onClick={clearRecentSearches}
                    className="text-[11px] font-medium normal-case tracking-normal hover:underline"
                    style={{ color: 'hsl(143, 28%, 30%)' }}
                    data-testid="recent-searches-clear"
                  >
                    {t('recentSearchesClear') || 'Clear'}
                  </button>
                </div>
                <ul className="max-h-64 overflow-y-auto">
                  {recentSearches.map((q) => (
                    <li key={q} className="flex items-stretch">
                      <button
                        type="button"
                        role="option"
                        onClick={() => {
                          setSearchQuery(q);
                          commitSearchToHistory(q); // bump it to the top
                          setShowRecentSearches(false);
                        }}
                        className="flex-1 text-left px-4 py-2 text-sm hover:bg-black/[.03] focus:bg-black/[.05] focus:outline-none"
                        style={{ color: 'hsl(143, 28%, 20%)' }}
                        data-testid={`recent-search-item-${q}`}
                      >
                        <span className="inline-flex items-center gap-2">
                          <Search className="w-3.5 h-3.5 opacity-60" aria-hidden="true" />
                          <span className="truncate">{q}</span>
                        </span>
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeRecentSearch(q);
                        }}
                        className="px-3 py-2 hover:bg-black/[.05] focus:outline-none"
                        aria-label={`${t('remove') || 'Remove'}: ${q}`}
                        data-testid={`recent-search-remove-${q}`}
                      >
                        <XIcon className="w-3.5 h-3.5 opacity-60" aria-hidden="true" />
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Dormancy banner — subtle nudge when one or more plants are entering
              their fall/winter rest period right now. Auto-hides when nothing is
              dormant, when the user is already viewing dormant-only, or when the
              banner has been dismissed for the current month. */}
          {dormantNowCount > 0 && !showDormantOnly && !dormancyBannerDismissed && (
            <div
              className="mt-3 flex items-start gap-2 rounded-lg px-3 py-2.5"
              style={{ backgroundColor: 'hsl(230, 60%, 97%)', border: '1px solid hsl(230, 40%, 88%)' }}
              role="status"
              aria-live="polite"
              data-testid="dormancy-banner"
            >
              <Moon className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: 'hsl(230, 40%, 45%)' }} aria-hidden="true" />
              <div className="flex-1 min-w-0">
                <p className="text-sm leading-snug" style={{ color: 'hsl(230, 40%, 25%)' }}>
                  <span className="font-medium">
                    {dormantNowCount === 1
                      ? (t('dormancyBannerOne') || '1 plant is in its dormancy window this month.')
                      : (t('dormancyBannerMany') || `${dormantNowCount} plants are in their dormancy window this month.`).replace('{count}', dormantNowCount)}
                  </span>{' '}
                  <button
                    onClick={() => setShowDormantOnly(true)}
                    className="underline underline-offset-2 hover:opacity-80"
                    style={{ color: 'hsl(230, 40%, 40%)' }}
                    data-testid="dormancy-banner-view"
                  >
                    {t('dormancyBannerAction') || 'Show them'}
                  </button>
                </p>
              </div>
              <button
                onClick={dismissDormancyBanner}
                className="flex-shrink-0 p-0.5 rounded hover:bg-white/60 transition-colors"
                style={{ color: 'hsl(230, 40%, 45%)' }}
                aria-label={t('dismiss') || 'Dismiss'}
                data-testid="dormancy-banner-dismiss"
              >
                <XIcon className="w-4 h-4" aria-hidden="true" />
              </button>
            </div>
          )}

          {/* Backfill prompt — first-time nudge that opens Settings › Fill
              missing information. Shown once per user when their library has
              plants with obvious empty care fields and they've never run the
              backfill. Amber-toned so it reads as a suggestion, not an
              error. */}
          {draftInfo && draftInfo.count > 0 && !draftBannerDismissed && (
            <div
              className="mt-3 flex items-start gap-2 rounded-lg px-3 py-2.5"
              style={{ backgroundColor: 'hsl(143, 30%, 96%)', border: '1px solid hsl(143, 28%, 80%)' }}
              role="status"
              aria-live="polite"
              data-testid="add-plant-draft-banner"
            >
              <Sprout className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: 'hsl(143, 28%, 30%)' }} aria-hidden="true" />
              <div className="flex-1 min-w-0">
                <p className="text-sm leading-snug" style={{ color: 'hsl(143, 28%, 22%)' }}>
                  <span className="font-medium">
                    {(t('addPlantDraftBanner') || 'You have {n} unfinished {plantWord} from last time.').replace('{n}', draftInfo.count).replace('{plantWord}', draftInfo.count === 1 ? (t('plantSingular') || 'plant') : (t('plantPlural') || 'plants'))}
                  </span>
                  {draftInfo.createdAt && (
                    <span
                      className="ml-1.5 text-xs"
                      style={{ color: 'hsl(125, 12%, 40%)' }}
                      data-testid="add-plant-draft-age"
                    >
                      · {formatRelativeTime(draftInfo.createdAt)}
                    </span>
                  )}
                </p>
              </div>
              <button
                type="button"
                onClick={async () => {
                  const { loadDraft } = await import('../utils/addPlantDraft');
                  const d = await loadDraft();
                  if (!d) { setDraftInfo(null); return; }
                  setResumeDraft(d);
                  setShowAddModal(true);
                }}
                className="text-xs px-2.5 py-1.5 rounded-md font-medium flex-shrink-0"
                style={{ backgroundColor: 'hsl(143, 28%, 25%)', color: 'white' }}
                data-testid="add-plant-draft-resume"
              >
                {t('resume') || 'Resume'}
              </button>
              <button
                type="button"
                onClick={async () => {
                  const { loadDraft, clearDraft, saveDraft } = await import('../utils/addPlantDraft');
                  const d = await loadDraft();
                  await clearDraft();
                  setDraftInfo(null);
                  toast(t('draftDiscarded') || 'Draft discarded', {
                    action: {
                      label: t('undo') || 'Undo',
                      onClick: async () => {
                        if (d && Array.isArray(d.files) && d.files.length) {
                          await saveDraft(d.mode, d.files, d.savedCount);
                          await _refreshDraft();
                        }
                      },
                    },
                  });
                }}
                className="text-xs px-2 py-1.5 flex-shrink-0"
                style={{ color: 'hsl(125, 12%, 35%)' }}
                data-testid="add-plant-draft-discard"
              >
                {t('discard') || 'Discard'}
              </button>
              <button
                type="button"
                onClick={() => setDraftBannerDismissed(true)}
                aria-label={t('dismiss') || 'Dismiss'}
                title={t('dismiss') || 'Dismiss'}
                className="p-1 flex-shrink-0 rounded-md hover:bg-black/5 transition-colors"
                style={{ color: 'hsl(125, 12%, 45%)' }}
                data-testid="add-plant-draft-dismiss"
              >
                <XIcon className="w-4 h-4" aria-hidden="true" />
              </button>
            </div>
          )}

          {showBackfillPrompt && (
            <div
              className="mt-3 flex items-start gap-2 rounded-lg px-3 py-2.5"
              style={{ backgroundColor: 'hsl(40, 60%, 96%)', border: '1px solid hsl(40, 40%, 85%)' }}
              role="status"
              aria-live="polite"
              data-testid="backfill-prompt-banner"
            >
              <Sparkles className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: 'hsl(21, 70%, 45%)' }} aria-hidden="true" />
              <div className="flex-1 min-w-0">
                <p className="text-sm leading-snug" style={{ color: 'hsl(21, 30%, 25%)' }}>
                  <span className="font-medium">
                    {plantsNeedingBackfillCount === 1
                      ? (t('backfillPromptOne') || '1 plant is missing some care details.')
                      : (t('backfillPromptMany') || `${plantsNeedingBackfillCount} plants are missing some care details.`).replace('{count}', plantsNeedingBackfillCount)}
                  </span>{' '}
                  <button
                    onClick={() => { dismissBackfillPrompt(); setShowSettingsModal(true); }}
                    className="underline underline-offset-2 hover:opacity-80"
                    style={{ color: 'hsl(21, 70%, 32%)' }}
                    data-testid="backfill-prompt-action"
                  >
                    {t('backfillPromptAction') || 'Fill them in →'}
                  </button>
                </p>
              </div>
              <button
                onClick={dismissBackfillPrompt}
                className="flex-shrink-0 p-0.5 rounded hover:bg-white/60 transition-colors"
                style={{ color: 'hsl(21, 30%, 45%)' }}
                aria-label={t('dismiss') || 'Dismiss'}
                data-testid="backfill-prompt-dismiss"
              >
                <XIcon className="w-4 h-4" aria-hidden="true" />
              </button>
            </div>
          )}

          {/* Family Grouping Tip — surfaces a friendly grouping suggestion
              when the user has 5+ plants sharing a botanical family.
              Dismissible per-family (localStorage). Renders nothing when
              no family meets the threshold. */}
          <FamilyGroupingTip plants={plants} onFilterFamily={setFamilyFilter} />

          {/* Last-synced timestamp — Drive-mode only. Subtle caption
              so users know how fresh the library is when the network
              is flaky. Auto-refreshes every 30s. */}
          <LastSyncedChip
            lastSyncedAt={lastSyncedAt}
            isLocalMode={isLocalMode}
            onRefresh={refreshPlants}
            refreshing={refreshing}
            isOffline={!isOnline}
            t={t}
          />

          {/* Active family filter chip — shown when the user tapped the
              AI grouping banner to focus on one family. Clearing it
              restores the full library. */}
          {familyFilter && (
            <div
              className="mt-3 flex items-center gap-2 rounded-lg px-3 py-2"
              style={{ backgroundColor: 'hsl(143, 45%, 96%)', border: '1px solid hsl(143, 45%, 78%)' }}
              role="status"
              aria-live="polite"
              data-testid="family-filter-chip"
            >
              <Sprout className="w-4 h-4 flex-shrink-0" style={{ color: 'hsl(143, 45%, 34%)' }} aria-hidden="true" />
              <span className="flex-1 min-w-0 text-sm" style={{ color: 'hsl(143, 28%, 22%)' }}>
                <span className="font-medium">
                  {(t('familyFilterActive') || 'Family: {family}').replace('{family}', familyFilter)}
                </span>
                <span className="ml-1 opacity-70">({filteredPlants.length})</span>
              </span>
              <button
                type="button"
                onClick={() => setFamilyFilter(null)}
                className="flex-shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium hover:bg-white/70 transition-colors"
                style={{ color: 'hsl(143, 45%, 30%)', border: '1px solid hsl(143, 45%, 70%)' }}
                aria-label={t('familyFilterClear') || 'Clear family filter'}
                data-testid="family-filter-clear"
              >
                <XIcon className="w-3 h-3" aria-hidden="true" />
                {t('familyFilterClear') || 'Clear family filter'}
              </button>
            </div>
          )}

          {/* Duplicate merge suggestions — surface up to 3 pairs of
              plants sharing a normalized common name so the user can
              merge them with one tap. Per-pair dismissal is persisted
              in localStorage so a "no thanks" doesn't nag forever. */}
          {(() => {
            // Master switch — user disabled the banner entirely from
            // Settings. Skip all detection work too.
            if (!dupeBannerEnabled) return null;
            // Snooze — user tapped "Hide for 7 days". Silent until the
            // deadline passes.
            if (dupeSnoozeUntil && Date.now() < dupeSnoozeUntil) return null;
            const activePlants = (plants || []).filter(p => !p.archived);
            // Fuzzy duplicate detection — matches close-but-not-identical
            // common names too. Tightened in iter 78 after user reports of
            // too-aggressive matches like "Coleus" ↔ "Coleus Wizard".
            // Signals used (any one qualifies):
            //   1) Normalised name is identical (existing behavior).
            //   2) Substring — but ONLY when the shorter side has ≥ 2
            //      significant tokens (so a bare genus like "Coleus" or
            //      "Rose" does not swallow every cultivar).
            //   3) Token overlap: same first significant token AND ≥ 2
            //      shared tokens AND Jaccard ≥ 0.66 (was 0.5).
            // Stop-words like "the", "a", "plant" are stripped before
            // tokenising so "Snake Plant" doesn't match "Snake".
            const STOP_TOKENS = new Set(['the', 'a', 'an', 'of', 'my', 'plant', 'plants', 'flower']);
            const norm = (s) => (s || '').trim().toLowerCase().replace(/\s+/g, ' ');
            const tokens = (s) => norm(s).split(' ').filter(t => t.length >= 2 && !STOP_TOKENS.has(t));
            const jaccard = (aArr, bArr) => {
              const a = new Set(aArr); const b = new Set(bArr);
              if (a.size === 0 && b.size === 0) return 0;
              let inter = 0; for (const t of a) if (b.has(t)) inter += 1;
              return inter / (a.size + b.size - inter);
            };
            const looksDupe = (a, b) => {
              const na = norm(a); const nb = norm(b);
              if (!na || !nb) return false;
              if (na === nb) return true;
              // 'exact' strictness — only identical normalised names count.
              if (dupeStrictness === 'exact') return false;
              const ta = tokens(na); const tb = tokens(nb);
              if (ta.length === 0 || tb.length === 0) return false;
              // Substring shortcut is skipped on 'strict' — user wants
              // both sides to genuinely share tokens, not just contain
              // the other name.
              if (dupeStrictness !== 'strict') {
                const shorter = ta.length <= tb.length ? ta : tb;
                const longer = ta.length <= tb.length ? tb : ta;
                if (shorter.length >= 2 && shorter.every(tk => longer.includes(tk))) return true;
              }
              // Token-overlap fallback — same leading token, ≥ 2 shared
              // significant tokens, and Jaccard ≥ threshold (0.66 balanced
              // vs 0.85 strict).
              if (ta[0] !== tb[0]) return false;
              const bSet = new Set(tb);
              let shared = 0; for (const tk of ta) if (bSet.has(tk)) shared += 1;
              if (shared < 2) return false;
              const jaccardThreshold = dupeStrictness === 'strict' ? 0.85 : 0.66;
              return jaccard(ta, tb) >= jaccardThreshold;
            };

            // Individual-plant blacklist derived from every pair the user
            // has permanently marked "Not a duplicate". Once a plant has
            // been said-no on with any partner, we stop suggesting it in
            // ANY future auto-pair — matches the user's mental model of
            // "I already told you these aren't duplicates, stop asking."
            // (Merge is still available via bulk-edit.)
            const permaNotDupePlantIds = new Set();
            for (const key of permanentlyNotDupePairs) {
              const parts = String(key).split('|');
              if (parts.length === 2) { permaNotDupePlantIds.add(parts[0]); permaNotDupePlantIds.add(parts[1]); }
            }

            const pairs = [];
            const usedIds = new Set();
            for (let i = 0; i < activePlants.length && pairs.length < 3; i++) {
              const a = activePlants[i];
              if (usedIds.has(a.plant_id)) continue;
              if (permaNotDupePlantIds.has(a.plant_id)) continue;
              if (!(a.common_name || '').trim()) continue;
              for (let j = i + 1; j < activePlants.length && pairs.length < 3; j++) {
                const b = activePlants[j];
                if (usedIds.has(b.plant_id)) continue;
                if (permaNotDupePlantIds.has(b.plant_id)) continue;
                if (!looksDupe(a.common_name, b.common_name)) continue;
                // Prefer the shorter name as the target (Keeper) so the
                // more specific listing is the one absorbed.
                const aName = (a.common_name || '').trim();
                const bName = (b.common_name || '').trim();
                const [target, source] = aName.length <= bName.length ? [a, b] : [b, a];
                const pairKey = [target.plant_id, source.plant_id].sort().join('|');
                if (dismissedDupePairs.has(pairKey)) continue;
                pairs.push({ target, source, pairKey });
                usedIds.add(a.plant_id); usedIds.add(b.plant_id);
                break;
              }
            }
            if (pairs.length === 0) return null;
            const persistPermanent = (pairKey) => {
              setPermanentlyNotDupePairs(prev => {
                const next = new Set(prev); next.add(pairKey);
                try { localStorage.setItem('not_dupe_pairs', JSON.stringify([...next])); } catch { /* quota — safe to ignore */ }
                return next;
              });
            };
            const persistReviewLater = (pairKey) => {
              setReviewLaterDupePairs(prev => {
                const next = new Set(prev); next.add(pairKey);
                try { localStorage.setItem('review_later_dupe_pairs', JSON.stringify([...next])); } catch { /* quota — safe to ignore */ }
                return next;
              });
            };
            return (
              <div className="space-y-2" data-testid="duplicate-suggestions">
                <div className="flex justify-end">
                  <div
                    className="inline-flex items-stretch rounded-full overflow-hidden"
                    style={{ border: '1px solid hsl(143, 28%, 70%)', backgroundColor: 'white' }}
                    data-testid="dupe-banner-snooze-group"
                  >
                    <button
                      type="button"
                      onClick={() => {
                        const until = Date.now() + 7 * 24 * 60 * 60 * 1000;
                        setDupeSnoozeUntil(until);
                        try { localStorage.setItem('dupe_banner_snoozed_until', String(until)); } catch { /* quota — safe */ }
                        toast.success((t('dupeSnoozeToast') || 'Duplicate suggestions hidden for {n} days').replace('{n}', 7));
                      }}
                      className="text-[11px] px-2.5 py-1 transition-colors hover:opacity-80"
                      style={{ color: 'hsl(143, 28%, 25%)' }}
                      data-testid="dupe-banner-snooze"
                      title={t('dupeSnoozeTip') || 'Hide these suggestions for 7 days'}
                    >
                      {t('dupeSnooze7d') || 'Hide for 7 days'}
                    </button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          type="button"
                          className="px-1.5 py-1 transition-colors hover:opacity-80 inline-flex items-center"
                          style={{ color: 'hsl(143, 28%, 25%)', borderLeft: '1px solid hsl(143, 28%, 82%)' }}
                          aria-label={t('dupeSnoozeMore') || 'More snooze options'}
                          data-testid="dupe-banner-snooze-more"
                        >
                          <ChevronDown className="w-3 h-3" aria-hidden="true" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" data-testid="dupe-banner-snooze-menu">
                        {[
                          { days: 1, key: 'dupeSnooze1d', fallback: 'Hide for 1 day' },
                          { days: 3, key: 'dupeSnooze3d', fallback: 'Hide for 3 days' },
                          { days: 7, key: 'dupeSnooze7d', fallback: 'Hide for 7 days' },
                          { days: 30, key: 'dupeSnooze30d', fallback: 'Hide for 30 days' },
                        ].map(({ days, key, fallback }) => (
                          <DropdownMenuItem
                            key={days}
                            onClick={() => {
                              const until = Date.now() + days * 24 * 60 * 60 * 1000;
                              setDupeSnoozeUntil(until);
                              try { localStorage.setItem('dupe_banner_snoozed_until', String(until)); } catch { /* quota — safe */ }
                              toast.success((t('dupeSnoozeToast') || 'Duplicate suggestions hidden for {n} days').replace('{n}', days));
                            }}
                            data-testid={`dupe-banner-snooze-${days}d`}
                          >
                            {t(key) || fallback}
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
                {pairs.map(({ target, source, pairKey }) => (
                  <DuplicateSuggestionCard
                    key={pairKey}
                    target={target}
                    source={source}
                    pairKey={pairKey}
                    isLocalMode={isLocalMode}
                    onMerge={(t, s) => { setMergeSource(s); setMergeTarget(t); }}
                    onDismissPermanent={persistPermanent}
                    onDismissReviewLater={persistReviewLater}
                  />
                ))}
              </div>
            );
          })()}

          {/* Sort / Group by — unified primary dropdown (iter 71).
              Replaces the A-Z / Grouped toggle + separate "by X" dropdown
              with a single picker: options include the two flat name sorts
              plus every grouping field. Alongside sits an optional
              "Then by…" secondary picker that promotes a name-sort into
              a grouped view when combined (e.g. Name (A→Z) + then by
              Family → Family groups with A-Z inside each). */}
          <div className="mt-3 flex items-center gap-1.5 sm:gap-2 flex-wrap" data-testid="view-mode-bar">
            {(() => {
              // Build the complete option list ONCE. Groups options are
              // sorted alphabetically by translated label so the picker
              // reads naturally in every language; the two name sorts sit
              // at the top so they feel primary.
              const SORT_OPTIONS = [
                { value: 'name_asc', labelKey: 'sortNameAsc', label: t('sortNameAsc') || 'Name (A → Z)', Icon: ArrowDownAZ, isFlat: true },
                { value: 'name_desc', labelKey: 'sortNameDesc', label: t('sortNameDesc') || 'Name (Z → A)', Icon: ArrowUpZA, isFlat: true },
                { value: 'date_added', labelKey: 'sortDateAdded', label: t('sortDateAdded') || 'Date Added (Newest)', Icon: Clock, isFlat: true },
                ...[...GROUP_OPTIONS]
                  .sort((a, b) => (t(a.labelKey) || a.labelKey).localeCompare(t(b.labelKey) || b.labelKey, undefined, { sensitivity: 'base' }))
                  .map(o => ({ value: o.value, labelKey: o.labelKey, label: t(o.labelKey), Icon: Layers, isFlat: false })),
              ];
              const currentPrimary = SORT_OPTIONS.find(o => o.value === sortBy) || SORT_OPTIONS[0];
              const currentSecondary = thenBy ? SORT_OPTIONS.find(o => o.value === thenBy) : null;
              const PrimaryIcon = currentPrimary.Icon;
              return (
                <>
                  {/* Primary dropdown */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border transition-all whitespace-nowrap flex-shrink-0"
                        style={{
                          backgroundColor: 'white',
                          borderColor: 'hsl(143, 28%, 25%)',
                          color: 'hsl(143, 28%, 25%)',
                          boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                        }}
                        aria-label={t('sortGroupBy') || 'Sort / Group by'}
                        data-testid="sort-by-dropdown-trigger"
                      >
                        <PrimaryIcon className="w-4 h-4" aria-hidden="true" />
                        <span>{currentPrimary.label}</span>
                        <ChevronDown className="w-3.5 h-3.5" aria-hidden="true" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="min-w-[200px]" data-testid="sort-by-dropdown-menu">
                      <DropdownMenuLabel>{t('sortGroupBy') || 'Sort / Group by'}</DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      {SORT_OPTIONS.map(opt => {
                        const OptIcon = opt.Icon;
                        const active = sortBy === opt.value;
                        return (
                          <DropdownMenuItem
                            key={opt.value}
                            onClick={() => setSortBy(opt.value)}
                            className="flex items-center gap-2"
                            style={{
                              color: active ? 'hsl(143, 28%, 25%)' : 'hsl(125, 12%, 32%)',
                              fontWeight: active ? 600 : 400,
                            }}
                            data-testid={`sort-by-option-${opt.value}`}
                          >
                            <OptIcon className="w-3.5 h-3.5" style={{ color: active ? 'hsl(143, 28%, 25%)' : 'hsl(125, 12%, 55%)' }} aria-hidden="true" />
                            <span className="flex-1">{opt.label}</span>
                          </DropdownMenuItem>
                        );
                      })}
                    </DropdownMenuContent>
                  </DropdownMenu>

                  {/* Secondary "Then by…" dropdown */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm border transition-all whitespace-nowrap flex-shrink-0"
                        style={{
                          backgroundColor: 'white',
                          borderColor: 'hsl(143, 28%, 85%)',
                          color: currentSecondary ? 'hsl(143, 28%, 25%)' : 'hsl(125, 12%, 45%)',
                          fontWeight: currentSecondary ? 500 : 400,
                        }}
                        aria-label={t('thenBy') || 'Then by'}
                        data-testid="then-by-dropdown-trigger"
                      >
                        <span>{currentSecondary ? currentSecondary.label : (t('thenByPlaceholder') || 'Then by…')}</span>
                        <ChevronDown className="w-3.5 h-3.5" aria-hidden="true" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="min-w-[200px]" data-testid="then-by-dropdown-menu">
                      <DropdownMenuLabel>{t('thenBy') || 'Then by'}</DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={() => setThenBy(null)}
                        className="text-xs"
                        style={{
                          color: !thenBy ? 'hsl(143, 28%, 25%)' : 'hsl(125, 12%, 45%)',
                          fontStyle: 'italic',
                        }}
                        data-testid="then-by-option-none"
                      >
                        {t('thenByNone') || 'None'}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      {SORT_OPTIONS
                        .filter(o => o.value !== sortBy)
                        .map(opt => {
                          const OptIcon = opt.Icon;
                          const active = thenBy === opt.value;
                          return (
                            <DropdownMenuItem
                              key={opt.value}
                              onClick={() => setThenBy(opt.value)}
                              className="flex items-center gap-2"
                              style={{
                                color: active ? 'hsl(143, 28%, 25%)' : 'hsl(125, 12%, 32%)',
                                fontWeight: active ? 600 : 400,
                              }}
                              data-testid={`then-by-option-${opt.value}`}
                            >
                              <OptIcon className="w-3.5 h-3.5" style={{ color: active ? 'hsl(143, 28%, 25%)' : 'hsl(125, 12%, 55%)' }} aria-hidden="true" />
                              <span className="flex-1">{opt.label}</span>
                            </DropdownMenuItem>
                          );
                        })}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </>
              );
            })()}

            {/* Unified Filter dropdown — consolidates the three previous
                pills (Terrarium, Dormant now, Needs dormancy) into a single
                menu so the header stays compact even as we add more smart
                filters. Multi-select via checkboxes; each option renders a
                live count so users see how many plants match before toggling.
                Options with a zero library-wide count are hidden (Dormant
                now + Needs dormancy) to avoid a menu full of "0" options
                on brand-new libraries. */}
            {(() => {
              const activeCount = (showTerrariumOnly ? 1 : 0) + (showDormantOnly ? 1 : 0) + (showNeedsDormancy ? 1 : 0);
              const hasAnyFilterableData = terrariumCount > 0 || dormantNowCount > 0 || needsDormancyCount > 0;
              if (!hasAnyFilterableData) return null;
              return (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border transition-all whitespace-nowrap flex-shrink-0"
                      style={{
                        backgroundColor: activeCount > 0 ? 'hsl(143, 28%, 25%)' : 'white',
                        borderColor: activeCount > 0 ? 'hsl(143, 28%, 25%)' : 'hsl(143, 28%, 85%)',
                        color: activeCount > 0 ? 'white' : 'hsl(143, 28%, 25%)',
                      }}
                      aria-label={t('filterBy') || 'Filter by'}
                      title={t('filterBy') || 'Filter by'}
                      data-testid="filter-by-dropdown-trigger"
                    >
                      <SlidersHorizontal className="w-3.5 h-3.5" aria-hidden="true" />
                      <span>{t('filter') || 'Filter'}</span>
                      {activeCount > 0 && (
                        <span
                          className="inline-flex items-center justify-center text-[11px] font-semibold rounded-full min-w-[18px] h-[18px] px-1"
                          style={{
                            backgroundColor: 'rgba(255,255,255,0.25)',
                            color: 'white',
                          }}
                          data-testid="filter-active-count"
                          aria-label={`${activeCount} active`}
                        >
                          {activeCount}
                        </span>
                      )}
                      <ChevronDown className="w-3.5 h-3.5" aria-hidden="true" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="min-w-[220px]" data-testid="filter-by-dropdown-menu">
                    <DropdownMenuLabel>{t('filterBy') || 'Filter by'}</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    {terrariumCount > 0 && (
                      <DropdownMenuCheckboxItem
                        checked={showTerrariumOnly}
                        onCheckedChange={() => setShowTerrariumOnly(!showTerrariumOnly)}
                        onSelect={(e) => e.preventDefault()}
                        data-testid="filter-terrarium-item"
                      >
                        <span className="flex items-center gap-2 flex-1">
                          <Box className="w-3.5 h-3.5" style={{ color: 'hsl(143, 28%, 45%)' }} aria-hidden="true" />
                          <span>{t('terrariumOnly') || 'Terrarium plants'}</span>
                        </span>
                        <span
                          className="ml-2 text-[11px] font-semibold px-1.5 py-0.5 rounded-full"
                          style={{ backgroundColor: 'hsl(143, 28%, 92%)', color: 'hsl(143, 28%, 25%)' }}
                          aria-label={`${terrariumCount} plants`}
                        >
                          {terrariumCount}
                        </span>
                      </DropdownMenuCheckboxItem>
                    )}
                    {dormantNowCount > 0 && (
                      <DropdownMenuCheckboxItem
                        checked={showDormantOnly}
                        onCheckedChange={() => setShowDormantOnly(!showDormantOnly)}
                        onSelect={(e) => e.preventDefault()}
                        data-testid="filter-dormant-now-item"
                      >
                        <span className="flex items-center gap-2 flex-1">
                          <Moon className="w-3.5 h-3.5" style={{ color: 'hsl(230, 40%, 55%)' }} aria-hidden="true" />
                          <span>{t('dormantNow') || 'Dormant now'}</span>
                        </span>
                        <span
                          className="ml-2 text-[11px] font-semibold px-1.5 py-0.5 rounded-full"
                          style={{ backgroundColor: 'hsl(230, 40%, 92%)', color: 'hsl(230, 40%, 40%)' }}
                          aria-label={`${dormantNowCount} plants`}
                        >
                          {dormantNowCount}
                        </span>
                      </DropdownMenuCheckboxItem>
                    )}
                    {needsDormancyCount > 0 && (
                      <DropdownMenuCheckboxItem
                        checked={showNeedsDormancy}
                        onCheckedChange={() => setShowNeedsDormancy(!showNeedsDormancy)}
                        onSelect={(e) => e.preventDefault()}
                        data-testid="filter-needs-dormancy-item"
                      >
                        <span className="flex items-center gap-2 flex-1">
                          <Moon className="w-3.5 h-3.5" style={{ color: 'hsl(260, 40%, 55%)' }} aria-hidden="true" />
                          <span>{t('needsDormancy') || 'Needs dormancy'}</span>
                        </span>
                        <span
                          className="ml-2 text-[11px] font-semibold px-1.5 py-0.5 rounded-full"
                          style={{ backgroundColor: 'hsl(260, 30%, 92%)', color: 'hsl(260, 40%, 40%)' }}
                          aria-label={`${needsDormancyCount} plants`}
                        >
                          {needsDormancyCount}
                        </span>
                      </DropdownMenuCheckboxItem>
                    )}
                    {activeCount > 0 && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => {
                            setShowTerrariumOnly(false);
                            setShowDormantOnly(false);
                            setShowNeedsDormancy(false);
                          }}
                          className="text-xs"
                          data-testid="filter-clear-all-item"
                        >
                          {t('clearFilters') || 'Clear all filters'}
                        </DropdownMenuItem>
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              );
            })()}

            {/* With photos / No photos — only meaningful in List size.
                Applies to both grouped AND alphabetical views: photos-off
                collapses each plant to a compact single-line text row so
                power users can scan long lists at a glance. Preference
                persisted in `localStorage.plant_grouped_thumbs`. */}
            {cardSize === 'list' && (
              <button
                onClick={toggleGroupedThumbs}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border transition-all whitespace-nowrap flex-shrink-0"
                style={{
                  backgroundColor: showGroupedThumbs ? 'hsl(143, 28%, 25%)' : 'white',
                  borderColor: showGroupedThumbs ? 'hsl(143, 28%, 25%)' : 'hsl(143, 28%, 85%)',
                  color: showGroupedThumbs ? 'white' : 'hsl(143, 28%, 25%)'
                }}
                aria-pressed={showGroupedThumbs}
                aria-label={showGroupedThumbs ? 'Hide thumbnails' : 'Show thumbnails'}
                title={showGroupedThumbs ? 'Hide thumbnails' : 'Show thumbnails'}
                data-testid="grouped-thumbs-toggle"
              >
                <ImageIcon className="w-3.5 h-3.5" aria-hidden="true" />
                <span className="hidden sm:inline">{showGroupedThumbs ? (t('withPhotos') || 'With photos') : (t('noPhotos') || 'No photos')}</span>
              </button>
            )}

            {/* Card Size Toggle — Normal / Compact / List. Shown for every
                view mode (iter 94). Grouped view now honours the same
                three sizes: Normal + Compact render as a card grid inside
                each group; List renders as single-line rows (with an
                optional thumbnail via the sibling With/No-photos toggle). */}
            <div className="inline-flex rounded-full p-0.5 ml-auto flex-shrink-0" role="group" aria-label="Card size" style={{ backgroundColor: 'hsl(143, 28%, 90%)' }}>
                {[
                  { id: 'normal', icon: LayoutGrid, label: t('normalCards') },
                  { id: 'compact', icon: Grid2x2, label: t('compactCards') },
                  { id: 'list', icon: List, label: t('listViewLabel') },
                ].map(opt => {
                  const Icon = opt.icon;
                  return (
                    <button
                      key={opt.id}
                      onClick={() => setCardSize(opt.id)}
                      className="p-1.5 rounded-full transition-all"
                      style={{
                        backgroundColor: cardSize === opt.id ? 'white' : 'transparent',
                        color: cardSize === opt.id ? 'hsl(143, 28%, 25%)' : 'hsl(125, 12%, 42%)',
                        boxShadow: cardSize === opt.id ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'
                      }}
                      aria-pressed={cardSize === opt.id}
                      aria-label={opt.label}
                      title={opt.label}
                      data-testid={`card-size-${opt.id}`}
                    >
                      <Icon className="w-4 h-4" aria-hidden="true" />
                    </button>
                  );
                })}
              </div>
          </div>

          {/* Active filter chips row (iter 73). Renders one small chip per
              active filter so users can see + remove filters individually
              without reopening the dropdown. Only appears when at least one
              filter is on. Each chip is dismissible via its own "×" button. */}
          {(showTerrariumOnly || showDormantOnly || showNeedsDormancy) && (
            <div className="mt-2 flex items-center gap-1.5 flex-wrap" data-testid="active-filter-chips-row">
              {showTerrariumOnly && (
                <span
                  className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full text-xs font-medium"
                  style={{
                    backgroundColor: 'hsl(143, 28%, 92%)',
                    color: 'hsl(143, 28%, 25%)',
                    border: '1px solid hsl(143, 28%, 82%)',
                  }}
                  data-testid="active-filter-chip-terrarium"
                >
                  <Box className="w-3 h-3" aria-hidden="true" />
                  <span>{t('terrariumOnly') || 'Terrarium plants'}</span>
                  <button
                    type="button"
                    onClick={() => setShowTerrariumOnly(false)}
                    className="ml-0.5 p-0.5 rounded-full hover:bg-white/70 transition-colors"
                    aria-label={`${t('remove') || 'Remove'} ${t('terrariumOnly') || 'Terrarium plants'}`}
                    data-testid="active-filter-chip-terrarium-remove"
                  >
                    <XIcon className="w-3 h-3" aria-hidden="true" />
                  </button>
                </span>
              )}
              {showDormantOnly && (
                <span
                  className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full text-xs font-medium"
                  style={{
                    backgroundColor: 'hsl(230, 40%, 94%)',
                    color: 'hsl(230, 40%, 30%)',
                    border: '1px solid hsl(230, 40%, 82%)',
                  }}
                  data-testid="active-filter-chip-dormant-now"
                >
                  <Moon className="w-3 h-3" aria-hidden="true" />
                  <span>{t('dormantNow') || 'Dormant now'}</span>
                  <button
                    type="button"
                    onClick={() => setShowDormantOnly(false)}
                    className="ml-0.5 p-0.5 rounded-full hover:bg-white/70 transition-colors"
                    aria-label={`${t('remove') || 'Remove'} ${t('dormantNow') || 'Dormant now'}`}
                    data-testid="active-filter-chip-dormant-now-remove"
                  >
                    <XIcon className="w-3 h-3" aria-hidden="true" />
                  </button>
                </span>
              )}
              {showNeedsDormancy && (
                <span
                  className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full text-xs font-medium"
                  style={{
                    backgroundColor: 'hsl(260, 30%, 94%)',
                    color: 'hsl(260, 40%, 30%)',
                    border: '1px solid hsl(260, 30%, 82%)',
                  }}
                  data-testid="active-filter-chip-needs-dormancy"
                >
                  <Moon className="w-3 h-3" aria-hidden="true" />
                  <span>{t('needsDormancy') || 'Needs dormancy'}</span>
                  <button
                    type="button"
                    onClick={() => setShowNeedsDormancy(false)}
                    className="ml-0.5 p-0.5 rounded-full hover:bg-white/70 transition-colors"
                    aria-label={`${t('remove') || 'Remove'} ${t('needsDormancy') || 'Needs dormancy'}`}
                    data-testid="active-filter-chip-needs-dormancy-remove"
                  >
                    <XIcon className="w-3 h-3" aria-hidden="true" />
                  </button>
                </span>
              )}
            </div>
          )}
        </div>
      </header>

      {/* Content */}
      <main id="plant-library-content" className="container mx-auto px-4 py-8" role="main" aria-label="Plant library">
        {loading ? (
          <div className={cardSize === 'compact' ? 'masonry-grid-compact' : 'masonry-grid'} data-testid="library-skeleton" aria-busy="true" aria-label={t('loading') || 'Loading'}>
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="masonry-item">
                <div className="rounded-2xl overflow-hidden bg-white" style={{ border: '1px solid hsl(143, 28%, 90%)' }}>
                  <div
                    className={`animate-pulse ${cardSize === 'compact' ? 'aspect-square' : 'aspect-[4/3]'}`}
                    style={{ backgroundColor: 'hsl(82, 45%, 90%)' }}
                  />
                  <div className="p-3 space-y-2">
                    <div className="h-4 rounded animate-pulse" style={{ width: `${70 - (i % 3) * 12}%`, backgroundColor: 'hsl(143, 20%, 90%)' }} />
                    <div className="h-3 rounded animate-pulse" style={{ width: '45%', backgroundColor: 'hsl(143, 20%, 93%)' }} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : filteredPlants.length === 0 && incompletePlants.length === 0 ? (
          <div className="flex flex-col items-center justify-center text-center" style={{ minHeight: 'calc(100vh - 280px)' }}>
            <Sprout className="w-16 h-16 mb-4" style={{ color: 'hsl(143, 28%, 25%)', opacity: 0.3 }} />
            <h2 className="text-xl font-semibold mb-2" style={{ color: 'hsl(143, 28%, 25%)' }}>
              {searchQuery ? t('noResults') : t('noPlants')}
            </h2>
            <p className="max-w-xs" style={{ color: 'hsl(125, 12%, 32%)' }}>
              {searchQuery
                ? t('tryDifferentSearch')
                : t('noPlantsInstruction')}
            </p>
          </div>
        ) : (
          <>
            {/* Incomplete Plants Section */}
            {/* Backfill Family & Type banner */}
            {plantsNeedingBackfill.length > 0 && !backfillDismissed && (
              <div className="mb-6 flex items-center gap-3 rounded-xl px-4 py-3" style={{ backgroundColor: 'hsl(143, 28%, 94%)', border: '1px solid hsl(143, 28%, 85%)' }} data-testid="backfill-banner">
                <Sparkles className="w-5 h-5 flex-shrink-0" style={{ color: 'hsl(143, 28%, 35%)' }} />
                <p className="text-sm flex-1" style={{ color: 'hsl(143, 28%, 25%)' }}>
                  <strong>{plantsNeedingBackfill.length}</strong> {plantsNeedingBackfill.length !== 1 ? t('plants') : t('plant')} {t('missingFamilyType')}
                </p>
                <Button
                  size="sm"
                  disabled={backfilling}
                  onClick={runBackfill}
                  style={{ backgroundColor: 'hsl(143, 28%, 25%)', color: 'white' }}
                  data-testid="backfill-button"
                >
                  {backfilling ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />{t('updating')}</> : t('updateAll')}
                </Button>
                <button
                  onClick={() => setBackfillDismissed(true)}
                  className="text-xs underline opacity-60 hover:opacity-100"
                  style={{ color: 'hsl(143, 28%, 25%)' }}
                  data-testid="backfill-dismiss"
                >
                  {t('dismiss')}
                </button>
              </div>
            )}

            {incompletePlants.length > 0 && (
              <div className="mb-8" data-testid="incomplete-section">
                <div className="flex items-center gap-3 mb-4">
                  <h2
                    className="text-base font-semibold px-3 py-1 rounded-full flex items-center gap-1.5"
                    style={{ backgroundColor: 'hsl(21, 70%, 92%)', color: 'hsl(21, 70%, 45%)' }}
                    data-testid="incomplete-header"
                  >
                    <Clock className="w-4 h-4" />
                    {t('needsAttention')}
                  </h2>
                  <span className="text-xs" style={{ color: 'hsl(125, 12%, 42%)' }}>
                    {incompletePlants.length} {incompletePlants.length !== 1 ? t('plants') : t('plant')} {t('toComplete')}
                  </span>
                  <button
                    onClick={handleClearIncomplete}
                    disabled={clearingIncomplete}
                    className="ml-auto inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full transition-colors disabled:opacity-50"
                    style={{ backgroundColor: 'hsl(0, 70%, 95%)', color: 'hsl(0, 70%, 42%)' }}
                    data-testid="clear-incomplete-button"
                  >
                    {clearingIncomplete
                      ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      : <Trash2 className="w-3.5 h-3.5" />}
                    {t('clearUnfinished') || 'Clear all'}
                  </button>
                </div>
                <div className="masonry-grid">
                  {incompletePlants.map((plant) => (
                    <div key={plant.plant_id} className="masonry-item">
                      <PlantCard
                        plant={plant}
                        onClick={() => handleEdit(plant)}
                        onEdit={handleEdit}
                        onDelete={handleDeleteClick}
                        onArchive={handleArchiveFromCard}
                        onCopyLink={handleCopyPlantLink}
                        isLocalMode={isLocalMode}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Complete Plants */}
            {filteredPlants.length > 0 && (
              <>
                {/* Count Bar & Per-Page Selector */}
                <div className="flex items-center justify-between mb-4" data-testid="plant-count-bar">
                  <span className="text-sm font-medium" style={{ color: 'hsl(143, 28%, 25%)' }} data-testid="total-plant-count">
                    {searchQuery.trim() ? (
                      <>{t('found')} {totalComplete} / {plants.filter(p => !p.incomplete).length} {plants.filter(p => !p.incomplete).length !== 1 ? t('plants') : t('plant')}</>
                    ) : (
                      <>{totalComplete} {totalComplete !== 1 ? t('plants') : t('plant')}</>
                    )}
                    {viewMode !== 'grouped' && totalComplete > perPage && (
                      <span style={{ color: 'hsl(125, 12%, 42%)' }}> — {t('showing')} {startIndex + 1}–{Math.min(startIndex + perPage, totalComplete)}</span>
                    )}
                  </span>
                  {viewMode !== 'grouped' && (
                    <div className="flex items-center gap-2">
                      <label className="text-xs" style={{ color: 'hsl(125, 12%, 42%)' }} htmlFor="per-page-select">{t('perPage')}</label>
                      <select
                        id="per-page-select"
                        value={perPage}
                        onChange={(e) => setPerPage(Number(e.target.value))}
                        className="text-sm rounded-lg border px-2 py-1"
                        style={{ borderColor: 'hsl(143, 28%, 85%)', color: 'hsl(143, 28%, 25%)', backgroundColor: 'white' }}
                        data-testid="per-page-select"
                      >
                        {(cardSize === 'list' ? LIST_PER_PAGE_OPTIONS : PER_PAGE_OPTIONS).map(n => (
                          <option key={n} value={n}>{n}</option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>

                {viewMode === 'grouped' ? (
                  <div data-testid="grouped-view">
                    {getGroupedPlants().map(group => (
                      <div key={group.label} className="mb-8" data-testid={`plant-group-${group.label}`}>
                        <div className="flex items-center gap-3 mb-4">
                          <h2
                            className="text-base font-semibold px-3 py-1 rounded-full"
                            style={{
                              backgroundColor: group.label === 'Not Specified' ? 'hsl(0, 0%, 92%)' : 'hsl(143, 28%, 90%)',
                              color: group.label === 'Not Specified' ? 'hsl(0, 0%, 45%)' : 'hsl(143, 28%, 25%)'
                            }}
                            data-testid="group-header"
                          >
                            {group.label}
                          </h2>
                          <span className="text-xs" style={{ color: 'hsl(125, 12%, 42%)' }}>
                            {group.plants.length} {group.plants.length !== 1 ? t('plants') : t('plant')}
                          </span>
                        </div>
                        {cardSize === 'list' ? (
                          <div className="rounded-xl overflow-hidden" style={{ backgroundColor: 'white', border: '1px solid hsl(143, 28%, 90%)' }}>
                            {group.plants.map((plant, idx) => (
                              showGroupedThumbs ? (
                                <PlantCard
                                  key={plant.plant_id}
                                  plant={plant}
                                  onClick={() => openPlantDetail(plant)}
                                  onEdit={handleEdit}
                                  onDelete={handleDeleteClick}
                                  onArchive={handleArchiveFromCard}
                                  onCopyLink={handleCopyPlantLink}
                                  isLocalMode={isLocalMode}
                                  listView={true}
                                  addedDateLabel={showDateAdded ? formatAddedDate(plant.created_at) : null}
                                  bulkMode={bulkMode}
                                  bulkSelected={bulkSelected.has(plant.plant_id)}
                                  onBulkToggle={toggleBulkSelect}
                                />
                              ) : (
                                <div
                                  key={plant.plant_id}
                                  className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors"
                                  style={{ borderTop: idx > 0 ? '1px solid hsl(143, 28%, 93%)' : 'none' }}
                                  data-testid={`group-list-item-${plant.plant_id}`}
                                >
                                  <button
                                    onClick={() => openPlantDetail(plant)}
                                    className="flex-1 min-w-0 flex items-center gap-3 text-left rounded focus-visible:ring-2 focus-visible:ring-inset"
                                    style={{ '--tw-ring-color': 'hsl(143, 40%, 42%)' }}
                                    data-testid={`group-list-open-${plant.plant_id}`}
                                  >
                                    <span className="text-sm font-medium truncate" style={{ color: 'hsl(143, 28%, 25%)' }}>
                                      {plant.common_name || t('unnamedPlant')}
                                    </span>
                                    {plant.scientific_name && (
                                      <span className="text-xs italic truncate hidden sm:inline" style={{ color: 'hsl(125, 12%, 42%)' }}>
                                        {plant.scientific_name}
                                      </span>
                                    )}
                                    {showDateAdded && formatAddedDate(plant.created_at) && (
                                      <span className="text-xs truncate hidden sm:inline flex-shrink-0" style={{ color: 'hsl(125, 12%, 50%)' }}>
                                        · {t('addedOn') || 'Added'} {formatAddedDate(plant.created_at)}
                                      </span>
                                    )}
                                  </button>
                                  {plant.incomplete && (
                                    <span className="text-xs px-2 py-0.5 rounded-full flex-shrink-0" style={{ backgroundColor: 'hsl(21, 70%, 95%)', color: 'hsl(21, 70%, 45%)' }}>
                                      {t('incomplete')}
                                    </span>
                                  )}
                                  {renderRowActions(plant)}
                                </div>
                              )
                            ))}
                          </div>
                        ) : (
                          // Normal / Compact — render a masonry card grid
                          // inside each group so users can eyeball plants
                          // per family (iter 94).
                          <div className={cardSize === 'compact' ? 'masonry-grid-compact' : 'masonry-grid'}>
                            {group.plants.map((plant) => (
                              <div key={plant.plant_id} className="masonry-item">
                                <PlantCard
                                  plant={plant}
                                  onClick={() => openPlantDetail(plant)}
                                  onEdit={handleEdit}
                                  onDelete={handleDeleteClick}
                                  onArchive={handleArchiveFromCard}
                                  onCopyLink={handleCopyPlantLink}
                                  isLocalMode={isLocalMode}
                                  compact={cardSize === 'compact'}
                                  addedDateLabel={showDateAdded ? formatAddedDate(plant.created_at) : null}
                                  bulkMode={bulkMode}
                                  bulkSelected={bulkSelected.has(plant.plant_id)}
                                  onBulkToggle={toggleBulkSelect}
                                />
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  cardSize === 'list' && !showGroupedThumbs ? (
                    // List + No photos (iter 94) — single-line text rows,
                    // fastest to scan for long libraries.
                    <div className="rounded-xl overflow-hidden" style={{ backgroundColor: 'white', border: '1px solid hsl(143, 28%, 90%)' }} data-testid="alphabetical-view">
                      {paginatedPlants.map((plant, idx) => (
                        <div
                          key={plant.plant_id}
                          className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors"
                          style={{ borderTop: idx > 0 ? '1px solid hsl(143, 28%, 93%)' : 'none' }}
                          data-testid={`flat-list-item-${plant.plant_id}`}
                        >
                          <button
                            onClick={() => openPlantDetail(plant)}
                            className="flex-1 min-w-0 flex items-center gap-3 text-left rounded focus-visible:ring-2 focus-visible:ring-inset"
                            style={{ '--tw-ring-color': 'hsl(143, 40%, 42%)' }}
                            data-testid={`flat-list-open-${plant.plant_id}`}
                          >
                            <span className="text-sm font-medium truncate" style={{ color: 'hsl(143, 28%, 25%)' }}>
                              {plant.common_name || t('unnamedPlant')}
                            </span>
                            {plant.scientific_name && (
                              <span className="text-xs italic truncate hidden sm:inline" style={{ color: 'hsl(125, 12%, 42%)' }}>
                                {plant.scientific_name}
                              </span>
                            )}
                            {showDateAdded && formatAddedDate(plant.created_at) && (
                              <span className="text-xs truncate hidden sm:inline flex-shrink-0" style={{ color: 'hsl(125, 12%, 50%)' }}>
                                · {t('addedOn') || 'Added'} {formatAddedDate(plant.created_at)}
                              </span>
                            )}
                          </button>
                          {plant.incomplete && (
                            <span className="text-xs px-2 py-0.5 rounded-full flex-shrink-0" style={{ backgroundColor: 'hsl(21, 70%, 95%)', color: 'hsl(21, 70%, 45%)' }}>
                              {t('incomplete')}
                            </span>
                          )}
                          {renderRowActions(plant)}
                        </div>
                      ))}
                    </div>
                  ) : (
                  <div className={getCardGridClass(cardSize)} style={cardSize === 'list' ? { backgroundColor: 'white', borderColor: 'hsl(143, 28%, 90%)' } : undefined} data-testid="alphabetical-view">
                    {paginatedPlants.map((plant) => (
                      <div key={plant.plant_id} className={cardSize === 'list' ? '' : 'masonry-item'}>
                        <PlantCard
                          plant={plant}
                          onClick={() => openPlantDetail(plant)}
                          onEdit={handleEdit}
                          onDelete={handleDeleteClick}
                          onArchive={handleArchiveFromCard}
                          onCopyLink={handleCopyPlantLink}
                          isLocalMode={isLocalMode}
                          compact={cardSize === 'compact'}
                          listView={cardSize === 'list'}
                          addedDateLabel={showDateAdded ? formatAddedDate(plant.created_at) : null}
                          bulkMode={bulkMode}
                          bulkSelected={bulkSelected.has(plant.plant_id)}
                          onBulkToggle={toggleBulkSelect}
                        />
                      </div>
                    ))}
                  </div>
                  )
                )}

                {/* Pagination Controls — only for A-Z view */}
                {viewMode !== 'grouped' && totalPages > 1 && (
                  <div className="flex items-center justify-center gap-3 mt-8" data-testid="pagination-controls">
                    <Button
                      variant="outline" size="sm"
                      disabled={safePage <= 1}
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      style={{ borderColor: 'hsl(143, 28%, 85%)', color: 'hsl(143, 28%, 25%)' }}
                      data-testid="pagination-prev"
                    >
                      <ChevronLeft className="w-4 h-4 mr-1" />
                      {t('previous')}
                    </Button>
                    <span className="text-sm font-medium" style={{ color: 'hsl(143, 28%, 25%)' }} data-testid="pagination-info">
                      {t('page')} {safePage} / {totalPages}
                    </span>
                    <Button
                      variant="outline" size="sm"
                      disabled={safePage >= totalPages}
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      style={{ borderColor: 'hsl(143, 28%, 85%)', color: 'hsl(143, 28%, 25%)' }}
                      data-testid="pagination-next"
                    >
                      {t('next')}
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </main>

      {/* Draggable FAB */}
      <div
        ref={fabRef}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        className="fixed z-50 touch-none select-none"
        style={{ right: `${fabPos.right}px`, bottom: `${fabPos.bottom}px` }}
        data-testid="add-plant-fab-wrapper"
      >
        <div
          className="rounded-full w-16 h-16 shadow-2xl flex items-center justify-center cursor-grab active:cursor-grabbing hover:scale-110 transition-transform"
          style={{ backgroundColor: 'hsl(21, 70%, 57%)', color: 'white' }}
          role="button" tabIndex={0}
          aria-label={t('addNewPlant')} title={t('addNewPlant')}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setShowAddModal(true); } }}
          data-testid="add-plant-fab"
        >
          <Plus className="w-8 h-8" aria-hidden="true" />
        </div>
      </div>

      {/* Migration Dialog */}
      {showMigration && (
        <MigrationDialog
          localPlantCount={localPlantCount}
          onComplete={() => { setShowMigration(false); reloadAfterMigration(); }}
          onDismiss={() => setShowMigration(false)}
        />
      )}

      {/* Modals */}
      {showAddModal && (
        <AddPlantModal
          onClose={() => { setShowAddModal(false); setEditingPlant(null); setSharedInitialFile(null); setResumeDraft(null); setDraftBannerDismissed(false); _refreshDraft(); }}
          onPlantAdded={handlePlantAdded}
          onEditPlant={(plantId) => { setShowAddModal(false); setEditingPlant(null); setPendingEditPlantId(plantId); onPlantAdded(); }}
          editPlant={editingPlant}
          existingPlants={plants}
          isLocalMode={isLocalMode}
          initialFile={sharedInitialFile}
          initialDraft={resumeDraft}
          pendingWorkRef={addModalPendingRef}
          onDelete={editingPlant ? (plantId) => { setShowAddModal(false); setEditingPlant(null); handleDeleteClick(plantId); } : undefined}
        />
      )}
      {/* Branded batch-exit AlertDialog — appears when the user swipes
          back mid-batch. Three-way choice: save-for-later keeps the
          draft in IDB so the Resume banner shows next visit; discard
          clears the draft; cancel keeps the modal open. Draft is
          auto-saved on every state change so "save for later" is
          effectively "just close the modal, the draft is already
          there". */}
      <AlertDialog open={batchExitDialog.open} onOpenChange={(v) => { if (!v) setBatchExitDialog({ open: false, pendingCount: 0 }); }}>
        <AlertDialogContent data-testid="batch-exit-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('batchExitTitle') || 'Leave the batch?'}</AlertDialogTitle>
            <AlertDialogDescription>
              {(t('batchExitBody') || 'You still have {n} {plantWord} to add. Save them for later, or discard the batch?').replace('{n}', batchExitDialog.pendingCount).replace('{plantWord}', batchExitDialog.pendingCount === 1 ? (t('plantSingular') || 'plant') : (t('plantPlural') || 'plants'))}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel data-testid="batch-exit-cancel">{t('keepEditing') || 'Keep editing'}</AlertDialogCancel>
            <AlertDialogAction
              data-testid="batch-exit-discard"
              onClick={async () => {
                const { clearDraft } = await import('../utils/addPlantDraft');
                await clearDraft();
                setBatchExitDialog({ open: false, pendingCount: 0 });
                setShowAddModal(false);
                _refreshDraft();
              }}
              style={{ backgroundColor: 'hsl(0, 65%, 45%)', color: 'white' }}
            >
              {t('discardBatch') || 'Discard'}
            </AlertDialogAction>
            <AlertDialogAction
              data-testid="batch-exit-save"
              onClick={() => {
                // Draft was already auto-saved on every state change, so
                // this is just a graceful close — the Resume banner will
                // appear on next Library mount.
                setBatchExitDialog({ open: false, pendingCount: 0 });
                setShowAddModal(false);
                _refreshDraft();
                toast.success(t('batchExitSavedToast') || 'Saved for later — resume from the library');
              }}
              style={{ backgroundColor: 'hsl(143, 28%, 25%)', color: 'white' }}
            >
              {t('saveForLater') || 'Save for later'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={clearIncompleteOpen} onOpenChange={setClearIncompleteOpen}>
        <AlertDialogContent data-testid="clear-incomplete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('clearUnfinishedTitle') || 'Clear unfinished entries?'}</AlertDialogTitle>
            <AlertDialogDescription>
              {(t('clearUnfinishedConfirm') || 'Remove all {n} unfinished {plantWord}? This can’t be undone.')
                .replace('{n}', incompletePlants.length)
                .replace('{plantWord}', incompletePlants.length === 1 ? (t('plantSingular') || 'entry') : (t('plantPlural') || 'entries'))}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel data-testid="clear-incomplete-cancel">{t('cancel') || 'Cancel'}</AlertDialogCancel>
            <AlertDialogAction
              data-testid="clear-incomplete-confirm"
              onClick={confirmClearIncomplete}
              style={{ backgroundColor: 'hsl(0, 65%, 45%)', color: 'white' }}
            >
              {t('clearUnfinished') || 'Clear all'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {selectedPlant && (
        <PlantDetailModal
          plant={selectedPlant}
          onClose={closePlantDetail}
          onEdit={(plant) => { closePlantDetail(); handleEdit(plant); }}
          onDelete={async (plantId) => { const ok = await onPlantDeleted(plantId); if (ok) closePlantDetail(); }}
          onPlantAdded={onPlantAdded}
          isLocalMode={isLocalMode}
          scrollY={scrollYRef.current}
          enlargedPhoto={enlargedPhoto}
          onEnlargedPhotoChange={setEnlargedPhoto}
          onShareThisPlant={(p) => { setShareChoicePlant(p); setShareChoiceOpen(true); closePlantDetail(); }}
          onShowQr={(p) => { closePlantDetail(); handleShowPlantQr(p); }}
        />
      )}

      {/* Merge target picker: shown when source is set but target isn't. */}
      {mergeSource && !mergeTarget && (
        <MergeTargetPicker
          sourcePlant={mergeSource}
          allPlants={plants}
          onPick={(t) => setMergeTarget(t)}
          onClose={() => setMergeSource(null)}
        />
      )}

      {/* Merge diff dialog: both plants set, user picks per-field. */}
      {mergeSource && mergeTarget && (
        <MergePlantsDialog
          target={mergeTarget}
          source={mergeSource}
          isLocalMode={isLocalMode}
          onClose={() => { setMergeSource(null); setMergeTarget(null); }}
          onMerged={async () => {
            setMergeSource(null); setMergeTarget(null);
            await onPlantAdded(); // re-load list
          }}
        />
      )}

      {showSettingsModal && <SettingsModal
        onClose={() => setShowSettingsModal(false)}
        isLocalMode={isLocalMode}
        pendingSync={pendingSync}
        pendingSyncCount={pendingSyncCount}
        pendingFlushing={pendingFlushing}
        onFlushPendingSync={flushPendingSync}
        onOpenPendingSync={() => { setShowSettingsModal(false); setPendingDialogOpen(true); }}
        onExportCSV={handleExportCSV}
        onExportPDF={handleExportPDF}
        onOpenTour={() => { setShowSettingsModal(false); setShowOnboardingTour(true); }}
        plants={plants}
        dupeBannerEnabled={dupeBannerEnabled}
        onToggleDupeBanner={(next) => {
          setDupeBannerEnabled(next);
          try { localStorage.setItem('dupe_banner_enabled', next ? 'true' : 'false'); } catch { /* quota — safe */ }
        }}
        dupeStrictness={dupeStrictness}
        onChangeDupeStrictness={(next) => {
          setDupeStrictness(next);
          try { localStorage.setItem('dupe_strictness', next); } catch { /* quota — safe */ }
        }}
        reviewLaterDupePairs={reviewLaterDupePairs}
        onRestoreDupePair={(pairKey) => {
          setReviewLaterDupePairs(prev => {
            const next = new Set(prev); next.delete(pairKey);
            try { localStorage.setItem('review_later_dupe_pairs', JSON.stringify([...next])); } catch { /* quota — safe */ }
            return next;
          });
        }}
        onPermaDismissDupePair={(pairKey) => {
          setReviewLaterDupePairs(prev => {
            const next = new Set(prev); next.delete(pairKey);
            try { localStorage.setItem('review_later_dupe_pairs', JSON.stringify([...next])); } catch { /* quota — safe */ }
            return next;
          });
          setPermanentlyNotDupePairs(prev => {
            const next = new Set(prev); next.add(pairKey);
            try { localStorage.setItem('not_dupe_pairs', JSON.stringify([...next])); } catch { /* quota — safe */ }
            return next;
          });
        }}
        permanentlyNotDupePairs={permanentlyNotDupePairs}
        onResetDupePairs={() => {
          setPermanentlyNotDupePairs(new Set());
          setReviewLaterDupePairs(new Set());
          try {
            localStorage.removeItem('not_dupe_pairs');
            localStorage.removeItem('review_later_pairs');
            localStorage.removeItem('review_later_dupe_pairs');
            localStorage.removeItem('dismissed_dupe_pairs');
          } catch { /* safe */ }
          toast.success(t('dupeResetToast') || 'Cleared all duplicate suggestions — starting fresh');
        }}
        isOwner={!!user?.is_owner}
      />}
      <OnboardingTour
        open={showOnboardingTour}
        onClose={() => setShowOnboardingTour(false)}
        isLocalMode={isLocalMode}
        onAddPlant={() => setShowAddModal(true)}
      />

      {qrInviteUrl && (
        <InviteQrModal url={qrInviteUrl} onClose={() => setQrInviteUrl(null)} />
      )}
      <ShareChoiceModal
        open={shareChoiceOpen}
        plant={shareChoicePlant}
        onClose={() => { setShareChoiceOpen(false); setShareChoicePlant(null); }}
        onShare={handleSharePicked}
      />

      {/* Logout Confirmation */}
      <AlertDialog open={showLogoutConfirm} onOpenChange={setShowLogoutConfirm}>
        <AlertDialogContent style={{ backgroundColor: 'white' }}>
          <AlertDialogHeader>
            <AlertDialogTitle style={{ color: 'hsl(143, 28%, 25%)', fontSize: '1.5rem' }}>
              {isLocalMode ? t('exitLocalMode') : t('logoutTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription style={{ color: 'hsl(125, 12%, 25%)', fontSize: '1rem' }}>
              {isLocalMode ? t('logoutConfirmLocal') : t('logoutConfirmDrive')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="logout-cancel-btn" style={{ borderColor: 'hsl(143, 28%, 85%)', color: 'hsl(143, 28%, 25%)' }}>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction data-testid="logout-confirm-btn" onClick={handleLogout} style={{ backgroundColor: 'hsl(143, 28%, 25%)', color: 'white' }}>
              {isLocalMode ? t('exit') : t('logoutTitle')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!plantToDelete} onOpenChange={() => setPlantToDelete(null)}>
        <AlertDialogContent style={{ backgroundColor: 'white' }}>
          <AlertDialogHeader>
            <AlertDialogTitle style={{ color: 'hsl(143, 28%, 25%)', fontSize: '1.5rem' }}>{t('deletePlant')}</AlertDialogTitle>
            <AlertDialogDescription style={{ color: 'hsl(125, 12%, 25%)', fontSize: '1rem' }}>
              {isLocalMode ? t('deleteConfirmLocal') : t('deleteConfirmDrive')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel style={{ borderColor: 'hsl(143, 28%, 85%)', color: 'hsl(143, 28%, 25%)' }}>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} style={{ backgroundColor: 'hsl(0, 70%, 50%)', color: 'white' }} data-testid="delete-confirm-btn">
              {t('delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Archive Dialog */}
      <ArchiveDialog
        isOpen={!!archivingPlant}
        plantName={archivingPlant?.common_name || ''}
        onConfirm={confirmArchive}
        onCancel={() => setArchivingPlant(null)}
      />

      {/* Bulk Edit Bar */}
      {bulkMode && (
        <BulkEditBar
          selectedIds={[...bulkSelected]}
          totalCount={filteredPlants.filter(p => !p.incomplete).length}
          onSelectAll={bulkSelectAll}
          onDeselectAll={bulkDeselectAll}
          onApply={handleBulkApply}
          onDelete={handleBulkDelete}
          onMerge={(ids) => {
            if (ids.length !== 2) return;
            const [a, b] = ids;
            const targetPlant = plants.find(p => p.plant_id === a);
            const sourcePlant = plants.find(p => p.plant_id === b);
            if (!targetPlant || !sourcePlant) { toast.error(t('mergePlantsNotFound') || 'Selected plants not found'); return; }
            setMergeSource(sourcePlant);
            setMergeTarget(targetPlant);
            cancelBulkMode();
          }}
          onCancel={cancelBulkMode}
          applying={bulkApplying}
          deleting={bulkDeleting}
        />
      )}
    </div>
  );
};
